import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/views-overview.css';
import './styles/views-facturacion.css';
import './styles/views-reportes.css';
import './styles/views-impuestos.css';
import './styles/views-conciliacion.css';
import './styles/views-estados-financieros.css';
import './styles/views-finiquitos.css';
import './styles/motion.css';
import './styles/themes.css';
import SummaryCards from './components/dashboard/SummaryCards';
import CreateInvoiceModal from './components/create-form/CreateInvoiceModal';
import OverviewView from './views/OverviewView';
import DatabasesView from './views/DatabasesView';
import FacturacionView from './views/FacturacionView';
import AprobadasView from './views/AprobadasView';
import ReportesView from './views/ReportesView';
import ImpuestosView from './views/ImpuestosView';
import ConciliacionBancariaView from './views/ConciliacionBancariaView';
import EstadosFinancierosView from './views/EstadosFinancierosView';
import FiniquitosView from './views/FiniquitosView';
import CatalogoSatView from './views/CatalogoSatView';
import {
  fetchAllAirtableRecords,
  createInvoiceRequest,
  updateAirtableRecord,
  uploadRecordAttachment,
  AIRTABLE_TABLES
} from './services/airtable';
import { sendApprovedRequestFilesToN8n, fetchN8nLogs } from './services/n8n';
import LoginPanel from './components/LoginPanel';
import { getCurrentSession, observeAuthState, signIn, signOut } from './services/auth';
import { useClientesDirectory, normalizeListaCtesId } from './hooks/useClientesDirectory';
import { useUsuariosDirectory } from './hooks/useUsuariosDirectory';
import { useCalendarioActivities } from './hooks/useCalendarioActivities';
import { useAttendanceReports } from './hooks/useAttendanceReports';
import { isFirebaseConfigured } from './services/firebase';
import {
  timeRanges,
  estadoFilterOptions,
  metodoPagoFilterOptions,
  contactoFilterOptions,
  pendientePagoOptions,
  requestsDataset,
  logsDataset,
  controlCenterBoards,
  taxUploadSummary,
  taxUploadMonths,
  taxUploadYears,
  taxDueLegend,
  taxUploadChecklist,
  activityStatusLegend,
  activityResponsablesDirectory,
  attendanceUsersMock,
  attendanceRecordsMock,
  attendanceTemplateOptions,
  attendanceUserTemplates,
  DEFAULT_ATTENDANCE_TEMPLATE_FILE
} from './constants/dashboardData';
import {
  airtableFieldIds,
  formaPagoOptions,
  FALLBACK_CLIENTS_TABLE_ID,
  clientFields,
  mockClientOptions,
  REGIMENES_TABLE_ID,
  USOS_CFDI_TABLE_ID,
  REGIMEN_TIPO_FIELD_ID,
  USO_CFDI_TIPO_FIELD_ID
} from './constants/formConfig';
import { formatCurrency, formatDate } from './utils/formatters';
import { getClientLabel, mapLinkedRecords, getCreateFormDefaults } from './utils/formHelpers';
import { normalizeEmail } from './utils/text';

const WHATSAPP_COUNTRY_PREFIX = '+52';
const WHATSAPP_PATTERN = /^\+52\d{10}$/;
const TAX_SECTION_ENABLED = process.env.NODE_ENV !== 'production';
const CONCILIATION_SECTION_ENABLED = process.env.NODE_ENV !== 'production';
const FINANCIAL_STATEMENTS_SECTION_ENABLED = process.env.NODE_ENV !== 'production';
const FINIQUITOS_SECTION_ENABLED =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_FINIQUITOS_SECTION_ENABLED !== 'false';
const CATALOGO_SAT_SECTION_ENABLED =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_CATALOGO_SAT_SECTION_ENABLED === 'true';
const DEV_ALL_ACCESS_ENABLED = process.env.NODE_ENV !== 'production';
const THEME_STORAGE_KEY = 'ci-dashboard-theme-mode';
const THEME_DARK = 'dark';
const THEME_LIGHT = 'light';
const SOLICITUDES_FIELD_IDS = {
  folio: 'fldGFQQM0UOq4lsga',
  nombreDelCliente: 'fldcURD1vkIvOxZHG',
  nombreDocumento: 'fldwDECl6K2XILbiM',
  idContribuyente: 'fld4VMH9ADVog4649',
  facturadoPor: 'fldA3esn5lAPfDHO2',
  fechaHoraSubidaFactura: 'fldI1wrXlkuj7q0we',
  enlaceDescargaFactura: 'fldRg3zoIDnYtg4Bg',
  enlaceDescargaContribuyente: 'fldAM43V4W6ooTMnq'
};

const getInitialThemeMode = () => {
  if (typeof window === 'undefined') return THEME_LIGHT;
  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === THEME_DARK || savedTheme === THEME_LIGHT) {
    document.documentElement.setAttribute('data-theme', savedTheme);
    return savedTheme;
  }
  const initialTheme = THEME_LIGHT;
  document.documentElement.setAttribute('data-theme', initialTheme);
  return initialTheme;
};

const normalizeAirtableTextValue = value => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    for (const item of value) {
      const normalizedItem = normalizeAirtableTextValue(item);
      if (normalizedItem) return normalizedItem;
    }
    return '';
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'string') {
        return parsed.trim();
      }
    } catch {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
};

const getAirtableTextField = (fields = {}, ...fieldKeys) => {
  for (const key of fieldKeys) {
    if (!key) continue;
    const candidate = normalizeAirtableTextValue(fields[key]);
    if (candidate) return candidate;
  }
  return '';
};

const getAirtableCheckboxField = (fields = {}, ...fieldKeys) => {
  for (const key of fieldKeys) {
    if (!key || !(key in fields)) continue;
    const candidate = fields[key];
    if (typeof candidate === 'boolean') return candidate;
    if (typeof candidate === 'number') return candidate !== 0;
    if (typeof candidate === 'string') {
      const normalized = candidate.trim().toLowerCase();
      if (['true', '1', 'si', 'sí', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'no', ''].includes(normalized)) return false;
    }
  }
  return false;
};

const isAdminRole = role => String(role || '').trim().toLowerCase() === 'administrador';

const parseDateValue = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const asText = String(value).trim();
  if (!asText) return null;
  const parsed = new Date(asText);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const onlyDateMatch = asText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!onlyDateMatch) return null;
  const normalized = new Date(`${onlyDateMatch[1]}-${onlyDateMatch[2]}-${onlyDateMatch[3]}T00:00:00`);
  return Number.isNaN(normalized.getTime()) ? null : normalized;
};

const getRangeStartDate = (rangeValue, now = new Date()) => {
  const base = new Date(now);
  if (Number.isNaN(base.getTime())) return null;
  base.setHours(0, 0, 0, 0);
  if (rangeValue === 'semana') {
    const day = base.getDay();
    const daysToMonday = day === 0 ? 6 : day - 1;
    base.setDate(base.getDate() - daysToMonday);
    return base;
  }
  if (rangeValue === 'mes') {
    base.setDate(1);
    return base;
  }
  if (rangeValue === 'anio') {
    base.setMonth(0, 1);
    return base;
  }
  return null;
};

const formatWhatsappValue = rawValue => {
  const digits = String(rawValue || '').replace(/\D/g, '');
  if (!digits) return '';

  let localDigits = digits;
  if (localDigits.length > 10 && localDigits.startsWith('52')) {
    localDigits = localDigits.slice(2);
  }
  if (localDigits.length > 10 && localDigits.startsWith('1')) {
    localDigits = localDigits.slice(1);
  }
  localDigits = localDigits.slice(0, 10);

  return localDigits ? `${WHATSAPP_COUNTRY_PREFIX}${localDigits}` : '';
};

function trimTrailingSlash(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/\/+$/, '');
}

function formatLogDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  } catch (error) {
    return date.toLocaleString();
  }
}

function computeDurationMs(start, stop) {
  if (!start) return null;
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return null;
  if (!stop) {
    return Math.max(Date.now() - startDate.getTime(), 0);
  }
  const stopDate = new Date(stop);
  if (Number.isNaN(stopDate.getTime())) return null;
  return Math.max(stopDate.getTime() - startDate.getTime(), 0);
}

function secondsToDuration(seconds) {
  if (!Number.isFinite(seconds)) return '';
  if (seconds < 1) return '0 s';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds ? `${minutes} min ${remainingSeconds} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const parts = [`${hours} h`];
  if (remainingMinutes) {
    parts.push(`${remainingMinutes} min`);
  }
  return parts.join(' ');
}

function normalizeComparableText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function compareTextLabels(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'es', { sensitivity: 'base' });
}

function getCompactPaymentMethodLabel(value) {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return 'Sin definir';
  const match = normalizedValue.match(/\(([^)]+)\)/);
  return match ? match[1] : normalizedValue;
}

function resolveInvoicePrimaryLink({ estado, downloadUrl, contribuyenteDownloadUrl }) {
  const normalizedStatus = normalizeComparableText(estado);
  if (!normalizedStatus) {
    return {
      url: '',
      label: ''
    };
  }
  if (['aprobada', 'pendiente'].includes(normalizedStatus) && contribuyenteDownloadUrl) {
    return {
      url: contribuyenteDownloadUrl,
      label: 'Abrir solicitud'
    };
  }
  if (['enviada', 'factura generada'].includes(normalizedStatus) && downloadUrl) {
    return {
      url: downloadUrl,
      label: 'Abrir carpeta'
    };
  }
  return {
    url: '',
    label: ''
  };
}

function canUploadApprovedInvoice(item) {
  return normalizeComparableText(item?.estado) === 'aprobada';
}

function getApprovedUploadBlockedReason(item) {
  const normalizedStatus = normalizeComparableText(item?.estado);
  if (normalizedStatus === 'factura generada') {
    return 'La factura ya fue generada y no admite una nueva carga de documentos.';
  }
  if (normalizedStatus === 'enviada') {
    return 'La factura ya fue enviada y no admite una nueva carga de documentos.';
  }
  if (normalizedStatus && normalizedStatus !== 'aprobada') {
    return 'Solo las solicitudes en estado Aprobada permiten adjuntar archivos desde este flujo.';
  }
  return '';
}

function hasInvoiceStatus(item, statuses = []) {
  if (!Array.isArray(statuses) || !statuses.length) return true;
  const currentStatus = normalizeComparableText(item?.estado);
  return statuses.some(status => currentStatus === normalizeComparableText(status));
}

function filterInvoicesByScope(list = [], scopeConfig = [], scopeKey = '') {
  const activeScope = scopeConfig.find(option => option.key === scopeKey);
  if (!activeScope || !Array.isArray(activeScope.statuses) || !activeScope.statuses.length) {
    return list;
  }
  return list.filter(item => hasInvoiceStatus(item, activeScope.statuses));
}

function buildDynamicFilterOptions(values = [], fallbackOptions = [], allLabel = 'Todos', preferredOrder = []) {
  const uniqueValues = Array.from(
    new Set(
      values
        .map(value => String(value || '').trim())
        .filter(Boolean)
    )
  );
  if (!uniqueValues.length) return fallbackOptions;

  const orderedValues = [
    ...preferredOrder.filter(value => uniqueValues.includes(value)),
    ...uniqueValues.filter(value => !preferredOrder.includes(value)).sort(compareTextLabels)
  ];
  return [
    { value: 'all', label: allLabel },
    ...orderedValues.map(value => ({ value, label: value }))
  ];
}

function buildResponsibleFilterOptions(list = []) {
  const seen = new Map();
  list.forEach(item => {
    const fallbackLabel = String(item?.responsableFilterLabel || item?.responsable || item?.responsableRaw || '').trim();
    const key = String(item?.responsableFilterKey || normalizeComparableText(fallbackLabel)).trim();
    const label = fallbackLabel;
    if (!key || !label || seen.has(key)) return;
    seen.set(key, label);
  });
  const sorted = Array.from(seen.entries()).sort((left, right) => compareTextLabels(left[1], right[1]));
  return [
    { value: 'all', label: 'Todo el equipo' },
    ...sorted.map(([value, label]) => ({ value, label }))
  ];
}

function matchesInvoiceSearch(item, query) {
  const normalizedQuery = normalizeComparableText(query);
  if (!normalizedQuery) return true;
  if (typeof item?.searchIndex === 'string' && item.searchIndex) {
    return item.searchIndex.includes(normalizedQuery);
  }
  return [
    item?.folio,
    item?.clienteVisible,
    item?.nombreContribuyente,
    item?.cliente,
    item?.idContribuyente,
    item?.responsable,
    item?.responsableRaw
  ]
    .map(normalizeComparableText)
    .some(value => value.includes(normalizedQuery));
}

function sortInvoiceRowsByActivity(list = []) {
  return [...list].sort((left, right) => {
    const leftTime = parseDateValue(left?.activityDateValue)?.getTime() || 0;
    const rightTime = parseDateValue(right?.activityDateValue)?.getTime() || 0;
    return rightTime - leftTime;
  });
}

function toPercent(numerator, denominator, fallback = 0) {
  if (!Number.isFinite(denominator) || denominator <= 0) return fallback;
  if (!Number.isFinite(numerator)) return 0;
  return Math.max(0, Math.min(100, Math.round((numerator / denominator) * 100)));
}

function formatLogDuration(start, stop, durationMs) {
  if (Number.isFinite(durationMs)) {
    return secondsToDuration(Math.max(Math.round(durationMs / 1000), 0));
  }
  const computed = computeDurationMs(start, stop);
  if (!Number.isFinite(computed)) return '';
  return secondsToDuration(Math.max(Math.round(computed / 1000), 0));
}

function pickEntryValue(entry, ...paths) {
  if (!entry || typeof entry !== 'object') return undefined;
  for (const path of paths) {
    const keys = Array.isArray(path) ? path : String(path || '').split('.');
    let candidate = entry;
    for (const key of keys) {
      if (!key) continue;
      if (candidate == null || typeof candidate !== 'object' || !(key in candidate)) {
        candidate = undefined;
        break;
      }
      candidate = candidate[key];
    }
    if (candidate !== undefined && candidate !== null) {
      return candidate;
    }
  }
  return undefined;
}

function mapN8nStatus(status) {
  const normalized = (status || '').toString().toLowerCase();
  if (['success', 'ok', 'completed', 'done', 'finished', 'true'].includes(normalized)) {
    return { label: 'OK', className: 'ok' };
  }
  if (['activo', 'active', 'activado', 'activando', 'en vivo'].includes(normalized)) {
    return { label: 'Activo', className: 'activo' };
  }
  if (['error', 'errored', 'failed', 'fail'].includes(normalized)) {
    return { label: 'Error', className: 'error' };
  }
  if (['canceled', 'cancelled', 'cancelado', 'cancelada', 'stopped'].includes(normalized)) {
    return { label: 'Cancelada', className: 'advertencia' };
  }
  if (['waiting', 'queued', 'pendiente'].includes(normalized)) {
    return { label: 'En espera', className: 'advertencia' };
  }
  if (['running', 'processing'].includes(normalized)) {
    return { label: 'En curso', className: 'advertencia' };
  }
  if (['warning', 'warn', 'advertencia'].includes(normalized)) {
    return { label: 'Advertencia', className: 'advertencia' };
  }
  if (!normalized && status === false) {
    return { label: 'En curso', className: 'advertencia' };
  }
  if (!normalized) {
    return { label: 'OK', className: 'ok' };
  }
  return { label: status, className: normalized };
}

function resolveWorkflowName(entry) {
  if (!entry || typeof entry !== 'object') return 'Workflow en n8n';
  const candidates = [
    entry.flujo,
    entry.titulo,
    entry.tituloWorkflow,
    entry.titulo_workflow,
    entry.etiqueta,
    entry.nombre,
    entry.workflowName,
    entry.workflow_name,
    entry.workflowTitle,
    entry.workflow_title,
    entry.workflowLabel,
    entry.workflow_label,
    entry.workflowDisplay,
    entry.workflow_display,
    entry.workflow?.displayName,
    entry.workflow?.title,
    entry.workflow,
    entry.workflow?.name,
    entry.workflow?.data?.name,
    entry.workflowData?.name,
    entry.workflowData?.data?.name,
    entry.workflowData?.workflow?.name,
    entry.workflow_data?.name,
    entry.workflowMetadata?.name,
    entry.workflow_meta?.name,
    entry.execution?.workflowName,
    entry.execution?.workflow?.name,
    entry.execution?.workflow?.displayName,
    entry.execution?.workflow?.title,
    entry.execution?.workflowDisplay,
    entry.execution?.workflowTitle,
    entry.execution?.workflowMetadata?.name,
    entry.execution?.workflow_meta?.name,
    entry.execution?.workflow?.meta?.name,
    entry.execution?.workflowData?.name,
    entry.execution?.workflow?.data?.name,
    entry.execution?.data?.workflow?.name,
    entry.data?.workflow?.name,
    entry.data?.workflowData?.name,
    entry.name,
    entry.workflowId && `Workflow ${entry.workflowId}`
  ];
  const found = candidates.find(value => typeof value === 'string' && value.trim());
  return found ? found.trim() : 'Workflow en n8n';
}

const N8N_APP_URL = process.env.REACT_APP_N8N_APP_URL || 'https://n8n.acnegocios.mx';
const N8N_APP_BASE = trimTrailingSlash(N8N_APP_URL);
const NOCODB_URL = process.env.REACT_APP_NOCODB_URL || 'https://ci.acnegocios.mx/db/dashboard/';
const VAULTWARDEN_URL = process.env.REACT_APP_VAULTWARDEN_URL || 'https://ci.acnegocios.mx/vaultwarden';
const AIRTABLE_BASE_URL =
  process.env.REACT_APP_AIRTABLE_BASE_URL ||
  (process.env.REACT_APP_AIRTABLE_BASE_ID
    ? `https://airtable.com/${process.env.REACT_APP_AIRTABLE_BASE_ID}`
    : 'https://airtable.com');
const N8N_LOGS_ENDPOINT =
  process.env.REACT_APP_N8N_LOGS_URL ||
  process.env.REACT_APP_N8N_LOGS_UR ||
  '';

const hasAirtableConfig = Boolean(
  process.env.REACT_APP_AIRTABLE_BASE_ID &&
  process.env.REACT_APP_AIRTABLE_TABLE_ID &&
  process.env.REACT_APP_AIRTABLE_TOKEN
);
const CALENDARIO_TABLE_ID = process.env.REACT_APP_AIRTABLE_CALENDARIO_TABLE_ID || 'tblKMgFs1u6MBbrz0';
const CLIENTES_TABLE_ID = process.env.REACT_APP_AIRTABLE_CLIENTES_TABLE_ID || FALLBACK_CLIENTS_TABLE_ID;
const USUARIOS_TABLE_ID = process.env.REACT_APP_AIRTABLE_USUARIOS_TABLE_ID || 'tblzPMTNbLb0suLaP';
const ASISTENCIAS_TABLE_ID = process.env.REACT_APP_AIRTABLE_ASISTENCIAS_TABLE_ID || 'tbl788wLi2KIfDYeK';
const ATTENDANCE_LOCATIONS_TABLE_ID = process.env.REACT_APP_AIRTABLE_ATTENDANCE_LOCATIONS_TABLE_ID || '';
const ATTENDANCE_LOCATION_FIELD_CONFIG = {
  nameField: process.env.REACT_APP_AIRTABLE_LOCATION_NAME_FIELD || 'Nombre',
  latField: process.env.REACT_APP_AIRTABLE_LOCATION_LAT_FIELD || 'Latitud',
  lngField: process.env.REACT_APP_AIRTABLE_LOCATION_LNG_FIELD || 'Longitud',
  radiusField: process.env.REACT_APP_AIRTABLE_LOCATION_RADIUS_FIELD || 'Radio',
  activeField: process.env.REACT_APP_AIRTABLE_LOCATION_ACTIVE_FIELD || 'Activo',
  activeValue: process.env.REACT_APP_AIRTABLE_LOCATION_ACTIVE_VALUE || '',
  notesField: process.env.REACT_APP_AIRTABLE_LOCATION_NOTES_FIELD || 'Notas'
};

const metodoPagoOptions = metodoPagoFilterOptions.filter(option => option.value !== 'all');
const contactoFormOptions = contactoFilterOptions.filter(option => option.value !== 'all');
const estadoSolicitudOptions = estadoFilterOptions.filter(option => option.value !== 'all');
const PANEL_SCOPE_CONFIG = [
  {
    key: 'operativas',
    label: 'En proceso',
    countLabel: 'Solicitudes operativas',
    statuses: ['Pendiente', 'Aprobada', 'Factura generada', 'Enviada']
  },
  {
    key: 'activas',
    label: 'Todas',
    countLabel: 'Solicitudes del rango'
  },
  {
    key: 'aprobadas',
    label: 'Listas para facturar',
    countLabel: 'Solo aprobadas',
    statuses: ['Aprobada']
  },
  {
    key: 'factura-generada',
    label: 'Factura generada',
    countLabel: 'Facturas generadas',
    statuses: ['Factura generada']
  },
  {
    key: 'enviadas',
    label: 'Enviadas',
    countLabel: 'Facturas enviadas',
    statuses: ['Enviada']
  }
];
const APPROVED_SCOPE_CONFIG = [
  {
    key: 'all-post-approval',
    label: 'Todas aprobadas y en proceso',
    countLabel: 'Flujo postaprobacion',
    statuses: ['Aprobada', 'Factura generada', 'Enviada']
  },
  {
    key: 'aprobadas',
    label: 'Solo aprobadas',
    countLabel: 'Aprobadas sin adjuntar',
    statuses: ['Aprobada']
  },
  {
    key: 'factura-generada',
    label: 'Factura generada',
    countLabel: 'Pendientes por enviar',
    statuses: ['Factura generada']
  },
  {
    key: 'enviadas',
    label: 'Enviadas',
    countLabel: 'Facturas enviadas',
    statuses: ['Enviada']
  }
];
const STATUS_FILTER_ORDER = [
  'Pendiente',
  'Aprobada',
  'Factura generada',
  'Enviada',
  'Cliente Notificado',
  'Rechazada'
];
const PAYMENT_STATUS_FILTER_ORDER = ['Pagado', 'Pendiente de Pago'];
const CONTACT_FILTER_ORDER = ['Correo Electrónico', 'WhatsApp', 'Ambos'];

const firebaseReady = isFirebaseConfigured();
const AIRTABLE_EMBED_URL = 'https://airtable.com/embed/app0T5u4xdwo80TOh/shr9Hf2eDqwp05PLy';
const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;

function buildWorkflowLink(explicitUrl, workflowSlug, executionId) {
  if (explicitUrl) return explicitUrl;
  if (!workflowSlug || !N8N_APP_BASE) return undefined;
  let link = `${N8N_APP_BASE}/workflow/${workflowSlug}`;
  if (executionId) {
    link += `?executionId=${executionId}`;
  }
  return link;
}

function extractWorkflowSlugFromUrl(url) {
  if (typeof url !== 'string') return '';
  const match = url.match(/\/workflow\/([^/?#]+)/i);
  return match ? match[1] : '';
}

function getWorkflowIdentifierCandidates(entry) {
  if (!entry || typeof entry !== 'object') return [];
  return [
    entry.workflowSlug,
    entry.workflow_slug,
    entry.workflowId,
    entry.workflow_id,
    entry.workflow?.id,
    entry.workflowData?.id,
    entry.workflow?.workflowId,
    entry.workflow?.workflow?.id,
    entry.execution?.workflow?.id,
    entry.execution?.workflowId,
    entry.execution?.workflow_id,
    entry.execution?.workflow?.workflowId,
    entry.execution?.workflow?.workflow_slug,
    entry.execution?.workflow?.slug,
    entry.execution?.workflow_slug,
    entry.data?.workflow?.id,
    entry.data?.workflowData?.id,
    entry.data?.workflowId,
    entry.execution?.workflowData?.id,
    entry.execution?.workflow?.data?.id,
    entry.execution?.workflow?.meta?.id,
    extractWorkflowSlugFromUrl(entry.url)
  ]
    .map(value => (value == null ? '' : String(value)))
    .filter(Boolean);
}

function normalizeN8nLogEntry(entry, index = 0) {
  if (!entry || typeof entry !== 'object') return null;
  const workflowIdCandidates = getWorkflowIdentifierCandidates(entry);
  const workflowSlug = workflowIdCandidates[0] || '';
  const workflowName = resolveWorkflowName(entry);
  const executionId =
    entry.executionId || entry.execution?.id || entry.id || entry.execution_id || `n8n-log-${index}`;
  const startedAt = pickEntryValue(
    entry,
    'startedAt',
    'startTime',
    'started_at',
    'inicio',
    'createdAt',
    'startedAtUtc',
    'start',
    'execution.startedAt',
    'execution.startTime',
    'execution.started_at',
    'execution.start'
  );
  const stoppedAt = pickEntryValue(
    entry,
    'stoppedAt',
    'stopTime',
    'stopped_at',
    'fin',
    'finishedAt',
    'stoppedAtUtc',
    'stop',
    'execution.stoppedAt',
    'execution.stopTime',
    'execution.stopped_at',
    'execution.stop'
  );
  const durationMsValue = (() => {
    const rawValue = pickEntryValue(entry, 'durationMs', 'execution.durationMs', 'execution.durationMilliseconds');
    if (rawValue == null) return undefined;
    const numeric = typeof rawValue === 'string' ? Number(rawValue) : rawValue;
    return Number.isFinite(numeric) ? numeric : undefined;
  })();
  const durationSecondsValue = (() => {
    const rawValue = pickEntryValue(
      entry,
      'duration',
      'durationSeconds',
      'execution.duration',
      'execution.durationSeconds'
    );
    if (rawValue == null) return undefined;
    const numeric = typeof rawValue === 'string' ? Number(rawValue) : rawValue;
    return Number.isFinite(numeric) ? numeric : undefined;
  })();
  const durationLabel =
    entry.duracion ||
    entry.durationLabel ||
    entry.duration_label ||
    formatLogDuration(startedAt, stoppedAt, durationMsValue) ||
    (Number.isFinite(durationSecondsValue) ? secondsToDuration(Math.round(durationSecondsValue)) : '');
  const rawStatus =
    entry.status ??
    entry.estado ??
    entry.estadoBruto ??
    entry.estado_bruto ??
    entry.execution?.status ??
    entry.execution?.state;
  const statusToMap = rawStatus ?? (entry.finished === false ? 'running' : 'success');
  const { label: estado, className: estadoClass } = mapN8nStatus(statusToMap);
  const message =
    entry.mensaje ||
    entry.mensajeUsuario ||
    entry.mensaje_usuario ||
    entry.resumen ||
    entry.resumenUsuario ||
    entry.resumen_usuario ||
    entry.message ||
    entry.execution?.message ||
    entry.summary ||
    entry.execution?.summary ||
    entry.notes ||
    entry.execution?.notes ||
    entry.description ||
    entry.descripcion ||
    entry.detailMessage ||
    entry.detalleMensaje ||
    (estadoClass === 'error'
      ? entry.error?.message || 'Ejecución con errores'
      : estadoClass === 'advertencia'
        ? 'Ejecución en curso'
        : 'Ejecución completada correctamente');
  const triggerOwner = pickEntryValue(
    entry,
    'triggeredBy',
    'trigger',
    'runBy',
    'owner',
    'startedBy',
    'operator',
    'execution.triggeredBy',
    'execution.trigger',
    'execution.runBy',
    'execution.owner',
    'execution.startedBy',
    'execution.operator'
  );
  let triggerLabel =
    pickEntryValue(
      entry,
      'triggerLabel',
      'execution.triggerLabel',
      'labelDisparo',
      'execution.labelDisparo',
      'disparador',
      'execution.disparador',
      'execution.trigger.label',
      'execution.trigger.name'
    ) || '';
  if (typeof triggerLabel === 'string') {
    const trimmedTriggerLabel = triggerLabel.trim();
    const placeholderTriggerLabel = /^(empty|vacío|sin etiqueta|sin label|none|n\/a)$/i;
    triggerLabel = trimmedTriggerLabel && !placeholderTriggerLabel.test(trimmedTriggerLabel)
      ? trimmedTriggerLabel
      : '';
  } else {
    triggerLabel = '';
  }
  const resolvedMode = entry.mode || entry.execution?.mode;
  if (!triggerLabel) {
    if (resolvedMode === 'manual') {
      triggerLabel = 'Ejecutado manualmente';
    } else if (resolvedMode) {
      triggerLabel = 'Ejecución automática';
    }
  }
  const startedLabel = formatLogDateTime(startedAt);
  const detailParts = [];
  if (triggerLabel) detailParts.push(triggerLabel);
  const triggerOwnerText =
    typeof triggerOwner === 'string'
      ? triggerOwner
      : triggerOwner && typeof triggerOwner === 'object'
        ? triggerOwner.name || triggerOwner.email || triggerOwner.id || ''
        : '';
  if (triggerOwnerText) detailParts.push(`Responsable: ${triggerOwnerText}`);
  if (startedLabel) detailParts.push(`Inició ${startedLabel}`);
  const detalle =
    entry.detalle ||
    entry.detalles ||
    entry.detalleUsuario ||
    entry.detalle_usuario ||
    entry.contexto ||
    entry.detail ||
    entry.execution?.detail ||
    entry.execution?.details ||
    detailParts.join(' · ');
  const workflowIdSource = pickEntryValue(
    entry,
    'workflowId',
    'workflow.id',
    'execution.workflowId',
    'execution.workflow.id',
    'execution.workflow.workflowId',
    'execution.workflow.slug'
  );
  const workflowLinkId = workflowSlug || workflowIdSource || entry.workflowId;
  const url = buildWorkflowLink(entry.url, workflowLinkId, executionId);
  return {
    id: String(executionId),
    estado,
    estadoClass,
    duracion: durationLabel || '—',
    startedAt,
    stoppedAt,
    flujo: workflowName,
    mensaje: message,
    url,
    detalle,
    resumen: entry.resumen || entry.resumenUsuario || '',
    triggerLabel,
    workflowId: workflowLinkId
  };
}

function normalizeN8nLogs(logs = []) {
  if (!Array.isArray(logs)) return [];
  const hiddenWorkflowIds = new Set(['fx1U2fRadSykkCE4']);
  const normalized = logs.map((entry, index) => normalizeN8nLogEntry(entry, index)).filter(Boolean);
  return normalized.filter(entry => {
      const workflowId = entry.workflowId || '';
      const workflowName = (entry.flujo || '').toLowerCase().trim();
      if (hiddenWorkflowIds.has(workflowId)) return false;
      if (workflowName.includes('ci-acnegocios.mx logs')) return false;
      if (entry.url && entry.url.includes('/workflow/fx1U2fRadSykkCE4')) return false;
      return true;
    });
}

function PersonIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5 19.25a7 7 0 0 1 14 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronDownIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M6.75 9.5 12 14.75 17.25 9.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M20 4v6h-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 10a8 8 0 1 0 1.2 6.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FilePlusIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 12v6M9 15h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [authReady, setAuthReady] = useState(!firebaseReady);
  const [authError, setAuthError] = useState('');
  const [range, setRange] = useState('semana');
  const [activeView, setActiveView] = useState('overview');
  const [summaryFilter, setSummaryFilter] = useState('operativas');
  const [approvedFilter, setApprovedFilter] = useState('all-post-approval');
  const [airtableRecords, setAirtableRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [errorRecords, setErrorRecords] = useState('');
  const [updatingRequestStatusById, setUpdatingRequestStatusById] = useState({});
  const [requestStatusFeedbackById, setRequestStatusFeedbackById] = useState({});
  const [updatingApprovedUserSentById, setUpdatingApprovedUserSentById] = useState({});
  const [approvedUserSentFeedbackById, setApprovedUserSentFeedbackById] = useState({});
  const [reloadIndex, setReloadIndex] = useState(0);
  const reloadTimerRef = useRef(null);
  const sessionMenuRef = useRef(null);
  const viewTabsRef = useRef(null);
  const [viewTabsOverflow, setViewTabsOverflow] = useState({
    left: false,
    right: false
  });
  const [isSessionMenuOpen, setIsSessionMenuOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingRecord, setCreatingRecord] = useState(false);
  const [createFeedback, setCreateFeedback] = useState('');
  const [createForm, setCreateForm] = useState(getCreateFormDefaults);
  const [createStep, setCreateStep] = useState(1);
  const [tableFilters, setTableFilters] = useState({
    estado: 'all',
    metodoPago: 'all',
    contacto: 'all',
    pendientePago: 'all',
    responsable: 'all'
  });
  const [approvedTableFilters, setApprovedTableFilters] = useState({
    estado: 'all',
    metodoPago: 'all',
    contacto: 'all',
    pendientePago: 'all',
    responsable: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [approvedSearchTerm, setApprovedSearchTerm] = useState('');
  const clientsEnabled = Boolean(session && hasAirtableConfig);
  const { clientOptions, clientDirectory, loadingClients, clientsError } = useClientesDirectory({
    enabled: clientsEnabled,
    clientsTableId: CLIENTES_TABLE_ID,
    mockClientOptions
  });
  const { userDirectory, loadingUsers } = useUsuariosDirectory({
    enabled: Boolean(session && hasAirtableConfig),
    usuariosTableId: USUARIOS_TABLE_ID
  });
  const normalizedSessionEmail = normalizeEmail(session?.email || '');
  const usersDirectoryList = useMemo(() => Object.values(userDirectory || {}), [userDirectory]);
  const currentUserDirectoryEntries = useMemo(() => {
    if (!normalizedSessionEmail) return [];
    return usersDirectoryList.filter(user => normalizeEmail(user?.correo) === normalizedSessionEmail);
  }, [usersDirectoryList, normalizedSessionEmail]);
  const sessionLinkedNames = useMemo(() => {
    const names = currentUserDirectoryEntries
      .map(user => String(user?.nombre || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [currentUserDirectoryEntries]);
  const sessionLinkedNamesLabel = sessionLinkedNames.join(' / ');
  const sessionGreetingName = sessionLinkedNamesLabel || session?.displayName || session?.email || 'equipo';
  const isCurrentUserAdmin = currentUserDirectoryEntries.some(user => isAdminRole(user?.rol));
  const hasFacturasPermission = currentUserDirectoryEntries.some(user => Boolean(user?.permisos?.facturas));
  const hasReportesPermission = currentUserDirectoryEntries.some(user => Boolean(user?.permisos?.reportes));
  const hasLinksPermission = currentUserDirectoryEntries.some(user => Boolean(user?.permisos?.enlaces));
  const hasFiniquitosPermission = currentUserDirectoryEntries.some(user => Boolean(user?.permisos?.finiquitos));
  const bypassPermissionChecks = DEV_ALL_ACCESS_ENABLED || !hasAirtableConfig;
  const canAccessLinks = isCurrentUserAdmin || hasLinksPermission;
  const canAccessFacturas = bypassPermissionChecks || isCurrentUserAdmin || hasFacturasPermission;
  const canAccessReportes = bypassPermissionChecks || isCurrentUserAdmin || hasReportesPermission;
  const canAccessTax = TAX_SECTION_ENABLED && (bypassPermissionChecks || isCurrentUserAdmin);
  const canAccessFiniquitos =
    FINIQUITOS_SECTION_ENABLED && (bypassPermissionChecks || isCurrentUserAdmin || hasFiniquitosPermission);
  const canOpenAirtable = Boolean(session);
  const overviewTitle = `Hola, ${sessionGreetingName}`;
  const availableViews = useMemo(() => {
    const views = ['overview'];
    if (CONCILIATION_SECTION_ENABLED) {
      views.push('conciliation');
    }
    if (FINANCIAL_STATEMENTS_SECTION_ENABLED) {
      views.push('financial-statements');
    }
    if (canAccessLinks) {
      views.push('databases');
    }
    if (canAccessFacturas) {
      views.push('panel', 'approved');
    }
    if (canAccessReportes) {
      views.push('reportes');
    }
    if (canAccessTax) {
      views.push('tax');
    }
    if (canAccessFiniquitos) {
      views.push('finiquitos');
    }
    if (CATALOGO_SAT_SECTION_ENABLED) {
      views.push('catalogo-sat');
    }
    return views;
  }, [canAccessFacturas, canAccessFiniquitos, canAccessLinks, canAccessReportes, canAccessTax]);
  const controlBoardsForCurrentUser = useMemo(() => {
    return controlCenterBoards.filter(board => {
      if (board.key === 'conciliation') return CONCILIATION_SECTION_ENABLED;
      if (board.key === 'panel') return canAccessFacturas;
      if (board.key === 'reportes') return canAccessReportes;
      if (board.key === 'tax') return canAccessTax;
      return false;
    });
  }, [canAccessFacturas, canAccessReportes, canAccessTax]);
  const overviewDescription = useMemo(() => {
    const linkedUsersMessage = sessionLinkedNames.length > 1
      ? `Tu correo esta enlazado a: ${sessionLinkedNamesLabel}.`
      : sessionLinkedNames.length === 1
        ? `Sesion activa como: ${sessionLinkedNamesLabel}.`
        : loadingUsers
          ? 'Validando tu perfil de usuario en Airtable.'
          : 'No encontramos un perfil enlazado a tu correo; se muestra la sesion activa.';
    const enabledModules = [];
    if (CONCILIATION_SECTION_ENABLED) enabledModules.push('Conciliacion');
    if (FINANCIAL_STATEMENTS_SECTION_ENABLED) enabledModules.push('Estados financieros');
    if (canAccessLinks) enabledModules.push('Enlaces');
    if (canAccessFacturas) enabledModules.push('Facturacion');
    if (canAccessReportes) enabledModules.push('Reportes');
    if (canAccessTax) enabledModules.push('Impuestos');
    if (canAccessFiniquitos) enabledModules.push('Finiquitos');
    if (!enabledModules.length) {
      return `${linkedUsersMessage} No tienes modulos activos. Solicita permisos a un administrador.`;
    }
    if (DEV_ALL_ACCESS_ENABLED) {
      return `${linkedUsersMessage} Modo desarrollo activo: se habilitan modulos operativos; Enlaces respeta permisos de Contraseñas CI.`;
    }
    return `${linkedUsersMessage} Funciones activas para tu usuario: ${enabledModules.join(', ')}.`;
  }, [
    canAccessFacturas,
    canAccessFiniquitos,
    canAccessLinks,
    canAccessReportes,
    canAccessTax,
    loadingUsers,
    sessionLinkedNames,
    sessionLinkedNamesLabel
  ]);
  const userInitialsByEmail = useMemo(() => {
    const directory = {};
    Object.values(userDirectory || {}).forEach(user => {
      if (!user) return;
      const email = user.correo;
      if (!email) return;
      directory[email] = user.iniciales || user.nombre || email;
    });
    return directory;
  }, [userDirectory]);
  const { activitySummaryData } = useCalendarioActivities({
    enabled: Boolean(session && hasAirtableConfig),
    calendarioTableId: CALENDARIO_TABLE_ID,
    clientDirectory,
    userDirectory,
    refreshIndex: reloadIndex
  });
  const {
    users: attendanceUsers,
    records: attendanceRecords,
    loading: loadingAttendance,
    error: attendanceError
  } = useAttendanceReports({
    enabled: Boolean(session && hasAirtableConfig && canAccessReportes),
    usuariosTableId: USUARIOS_TABLE_ID,
    asistenciasTableId: ASISTENCIAS_TABLE_ID,
    refreshIndex: reloadIndex
  });
  const [clientSearch, setClientSearch] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [regimenOptions, setRegimenOptions] = useState([]);
  const [usoCfdiOptions, setUsoCfdiOptions] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [catalogsError, setCatalogsError] = useState('');
  const [fileInputsKey, setFileInputsKey] = useState(0);
  const [showAirtableEmbed, setShowAirtableEmbed] = useState(false);
  const [showAutomationUploadModal, setShowAutomationUploadModal] = useState(false);
  const [selectedApprovedRequest, setSelectedApprovedRequest] = useState(null);
  const [xmlAutomationFile, setXmlAutomationFile] = useState(null);
  const [pdfAutomationFile, setPdfAutomationFile] = useState(null);
  const [uploadingAutomation, setUploadingAutomation] = useState(false);
  const [uploadAutomationFeedback, setUploadAutomationFeedback] = useState('');
  const mockRequests = useMemo(() => requestsDataset[range], [range]);
  const [rawLogs, setRawLogs] = useState(() => logsDataset[range] || []);
  const logs = useMemo(() => {
    const normalizedLogs = normalizeN8nLogs(rawLogs);
    const rangeStart = getRangeStartDate(range);
    if (!rangeStart) return normalizedLogs;
    return normalizedLogs.filter(item => {
      const eventDate = parseDateValue(item.startedAt || item.stoppedAt);
      if (!eventDate) return true;
      return eventDate >= rangeStart;
    });
  }, [rawLogs, range]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [logsReloadToken, setLogsReloadToken] = useState(0);
  const [showAutomationPulse, setShowAutomationPulse] = useState(false);
  const isDarkTheme = themeMode === THEME_DARK;
  const sessionUserEmail = session?.email || session?.displayName || 'Sin correo';
  const userMenuScreens = useMemo(() => [], []);

  const activeLabel = timeRanges.find(item => item.key === range)?.label;
  const today = new Date();
  const todayMonthKey = String(today.getMonth() + 1).padStart(2, '0');
  const todayYear = today.getFullYear();

  useEffect(() => {
    console.group('[Debug] Configuración inicial');
    console.info('[Debug] Airtable listo:', hasAirtableConfig);
    console.info('[Debug] N8N App URL:', N8N_APP_URL);
    console.info('[Debug] Webhook de logs configurado:', Boolean(N8N_LOGS_ENDPOINT));
    console.info('[Debug] Webhook de automatización configurado:', Boolean(N8N_WEBHOOK_URL));
    console.groupEnd();
  }, [hasAirtableConfig]);

  useEffect(() => {
    if (!firebaseReady) return undefined;
    let unsubscribe;

    try {
      setSession(getCurrentSession());
      unsubscribe = observeAuthState(user => {
        setSession(user);
        setAuthReady(true);
        setAuthError('');
      });
    } catch (error) {
      console.error('[Firebase Auth] No fue posible iniciar el listener:', error);
      setAuthError(error.message);
      setAuthReady(true);
    }

    return () => {
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSessionMenuOpen || typeof document === 'undefined') return undefined;

    const handleOutsideClick = event => {
      if (!sessionMenuRef.current?.contains(event.target)) {
        setIsSessionMenuOpen(false);
      }
    };

    const handleEscKey = event => {
      if (event.key === 'Escape') {
        setIsSessionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isSessionMenuOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', themeMode);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch (error) {
      console.warn('[Theme] No fue posible guardar el tema:', error);
    }
  }, [themeMode]);

  useEffect(() => {
    if (availableViews.includes(activeView)) return;
    setActiveView('overview');
  }, [activeView, availableViews]);

  useEffect(() => {
    if (canOpenAirtable) return;
    setShowAirtableEmbed(false);
  }, [canOpenAirtable]);

  useEffect(() => {
    if (session) return;
    setIsSessionMenuOpen(false);
  }, [session]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const tabsNode = viewTabsRef.current;
    if (!tabsNode) return undefined;

    const computeOverflow = () => {
      const left = tabsNode.scrollLeft > 4;
      const right = tabsNode.scrollLeft + tabsNode.clientWidth < tabsNode.scrollWidth - 4;
      setViewTabsOverflow(prev => {
        if (prev.left === left && prev.right === right) return prev;
        return { left, right };
      });
    };

    computeOverflow();
    tabsNode.addEventListener('scroll', computeOverflow, { passive: true });
    window.addEventListener('resize', computeOverflow);

    return () => {
      tabsNode.removeEventListener('scroll', computeOverflow);
      window.removeEventListener('resize', computeOverflow);
    };
  }, [availableViews, activeView, canAccessFacturas, canAccessLinks, canAccessReportes, canAccessTax]);

  const handleRefreshLogs = () => {
    setLogsReloadToken(prev => prev + 1);
  };

  const handleToggleTheme = () => {
    setThemeMode(prev => (prev === THEME_DARK ? THEME_LIGHT : THEME_DARK));
  };

  const themeToggleButton = (
    <button
      className="theme-toggle-corner"
      type="button"
      onClick={handleToggleTheme}
      aria-pressed={isDarkTheme}
      aria-label={`Cambiar a modo ${isDarkTheme ? 'claro' : 'oscuro'}`}
      title={`Cambiar a modo ${isDarkTheme ? 'claro' : 'oscuro'}`}
    >
      <span aria-hidden="true">{isDarkTheme ? '☾' : '☀'}</span>
    </button>
  );

  useEffect(() => {
    if (activeView !== 'panel' || !showAutomationPulse) return undefined;
    let ignore = false;

    async function loadLogs() {
      setLoadingLogs(true);
      setLogsError('');
      setRawLogs(logsDataset[range] || []);
      try {
        console.log('[n8n] Cargando logs para rango:', range);
        const data = await fetchN8nLogs(range);
        if (!ignore) {
          const fromArray = Array.isArray(data) ? data : null;
          const fromData = !fromArray && Array.isArray(data?.data) ? data.data : null;
          const fromLogs = !fromArray && !fromData && Array.isArray(data?.logs) ? data.logs : null;
          const payload = fromArray || fromData || fromLogs || [];
          console.log('[n8n] Logs recibidos desde API:', payload.length);
          setRawLogs(payload);
          if (!payload.length) {
            setLogsError('n8n respondió sin ejecuciones para el rango seleccionado.');
          }
        }
      } catch (error) {
        console.error('[n8n] No fue posible cargar los logs:', error);
        if (!ignore) {
          setRawLogs(logsDataset[range] || []);
          setLogsError('No fue posible cargar las ejecuciones de n8n, usando datos de ejemplo.');
        }
      } finally {
        if (!ignore) {
          setLoadingLogs(false);
        }
      }
    }

    loadLogs();

    return () => {
      ignore = true;
    };
  }, [range, logsReloadToken, activeView, showAutomationPulse]);

  useEffect(() => {
    if (!session || !hasAirtableConfig || !canAccessFacturas) {
      setAirtableRecords([]);
      setLoadingRecords(false);
      setErrorRecords('');
      return;
    }

    let ignore = false;
    async function loadRecords() {
      try {
        setLoadingRecords(true);
        setErrorRecords('');
        const requestOptions = {
          maxRecords: 5000,
          sort: [{ field: 'Fecha y hora de subida de factura', direction: 'desc' }]
        };
        console.log('[Dashboard] Solicitando todas las solicitudes a Airtable:', requestOptions);
        const records = await fetchAllAirtableRecords(requestOptions);
        if (!ignore) {
          console.log('[Dashboard] Airtable devolvió registros:', records.length);
          setAirtableRecords(records);
        }
      } catch (error) {
        if (!ignore) {
          setErrorRecords(error.message);
        }
      } finally {
        if (!ignore) {
          setLoadingRecords(false);
        }
      }
    }

    loadRecords();
    return () => {
      ignore = true;
    };
  }, [reloadIndex, session, hasAirtableConfig, canAccessFacturas]);

  useEffect(() => {
    if (!session || !hasAirtableConfig || !canAccessFacturas) {
      setRegimenOptions([]);
      setUsoCfdiOptions([]);
      setLoadingCatalogs(false);
      setCatalogsError('');
      return;
    }

    let ignore = false;
    const tipo = createForm.tipoContribuyente;
    const buildFormula = fieldId => {
      if (!tipo) return undefined;
      const sanitized = tipo.replace(/"/g, '\\"');
      return `SEARCH(\"${sanitized}\",{${fieldId}})`;
    };

    async function loadLinkedCatalogs() {
      try {
        setLoadingCatalogs(true);
        setCatalogsError('');
        console.log('[Create Form] Solicitando catálogos vinculados para tipo:', tipo || 'sin tipo');
        const [regimenRecords, usoRecords] = await Promise.all([
          fetchAllAirtableRecords({
            tableId: REGIMENES_TABLE_ID,
            maxRecords: 500,
            filterByFormula: buildFormula(REGIMEN_TIPO_FIELD_ID)
          }),
          fetchAllAirtableRecords({
            tableId: USOS_CFDI_TABLE_ID,
            maxRecords: 500,
            filterByFormula: buildFormula(USO_CFDI_TIPO_FIELD_ID)
          })
        ]);
        if (ignore) return;
        console.log('[Create Form] Catálogos recibidos:', {
          regimenes: regimenRecords?.length || 0,
          usos: usoRecords?.length || 0
        });
        setRegimenOptions(mapLinkedRecords(regimenRecords));
        setUsoCfdiOptions(mapLinkedRecords(usoRecords));
      } catch (error) {
        if (!ignore) {
          setCatalogsError(error.message);
        }
      } finally {
        if (!ignore) {
          setLoadingCatalogs(false);
        }
      }
    }

    loadLinkedCatalogs();
    return () => {
      ignore = true;
    };
  }, [session, hasAirtableConfig, createForm.tipoContribuyente, canAccessFacturas]);

  const normalizedAirtableRows = useMemo(() => {
    const rows = airtableRecords.map(record => {
      const fields = record.fields || {};
      const downloadUrl = getAirtableTextField(
        fields,
        'Enlace descarga factura',
        SOLICITUDES_FIELD_IDS.enlaceDescargaFactura
      );
      const contribuyenteDownloadUrl = getAirtableTextField(
        fields,
        'Enlace descarga contribuyente',
        SOLICITUDES_FIELD_IDS.enlaceDescargaContribuyente
      );
      const clienteAsociadoField =
        fields['Cliente asociado (Lista Ctes Fact)'] ||
        fields['Cliente Asociado'] ||
        fields[airtableFieldIds.clienteAsociado];
      const clienteAsociadoId = Array.isArray(clienteAsociadoField) ? clienteAsociadoField[0] : '';
      const clientFromDirectory = clienteAsociadoId ? clientDirectory[clienteAsociadoId] : null;
      const empresaFromDirectory = clientFromDirectory ? clientFromDirectory.empresa : '';
      const aliasFromDirectory = clientFromDirectory ? clientFromDirectory.alias : '';
      const nombreRazonSocial = getAirtableTextField(
        fields,
        'Nombre o Razón Social',
        airtableFieldIds.nombreRazon
      );
      const nombreContribuyenteFromRecord = getAirtableTextField(
        fields,
        'Nombre Del Cliente',
        SOLICITUDES_FIELD_IDS.nombreDelCliente
      );
      const nombreDocumento = getAirtableTextField(
        fields,
        'Nombre documento',
        SOLICITUDES_FIELD_IDS.nombreDocumento
      );
      const idContribuyente = getAirtableTextField(
        fields,
        'ID Contribuyente',
        SOLICITUDES_FIELD_IDS.idContribuyente
      );
      const nombreContribuyente =
        nombreContribuyenteFromRecord ||
        aliasFromDirectory ||
        clientFromDirectory?.nombreComercial ||
        empresaFromDirectory ||
        idContribuyente;
      const empresa = empresaFromDirectory || nombreContribuyente || nombreRazonSocial || 'Sin nombre';
      const aliasFromRecord = fields.Alias || fields['Alias (Lista Ctes Fact)'] || '';
      const clienteAlias = aliasFromDirectory || aliasFromRecord || '';
      const listaCtesIdCliente =
        clientFromDirectory?.idCliente ||
        normalizeListaCtesId(
          getAirtableTextField(fields, 'ID Cliente', 'ID Contribuyente', SOLICITUDES_FIELD_IDS.idContribuyente)
        ) ||
        '';
      const responsableField = fields['Facturado por'] || fields[SOLICITUDES_FIELD_IDS.facturadoPor];
      const responsableRaw =
        typeof responsableField === 'string'
          ? responsableField
          : Array.isArray(responsableField)
            ? responsableField.join(', ')
            : '';
      const responsableNormalized = normalizeEmail(
        Array.isArray(responsableField) ? responsableField[0] : responsableField
      );
      const responsableDisplay =
        userInitialsByEmail[responsableNormalized] || responsableRaw || 'Sin asignar';
      const totalValue = fields.Total ?? fields[airtableFieldIds.total];
      const totalNumber = typeof totalValue === 'number' ? totalValue : Number(totalValue);
      const estado = getAirtableTextField(fields, 'Estado de Solicitud', airtableFieldIds.estado) || 'Sin estado';
      const estadoKey = normalizeComparableText(estado);
      const enviadoPorUsuario = getAirtableCheckboxField(
        fields,
        'Enviado por Usuario',
        airtableFieldIds.enviadoPorUsuario
      );
      const canToggleUserSent = ['factura generada', 'enviada'].includes(estadoKey);
      const contacto = getAirtableTextField(fields, 'Método de Contacto', airtableFieldIds.contacto) || 'N/D';
      const metodoPago = getAirtableTextField(fields, 'Método de Pago', airtableFieldIds.metodoPago) || 'Sin definir';
      const pendientePago = getAirtableTextField(fields, 'Pendiente de pago', airtableFieldIds.pendientePago) || 'Sin definir';
      const rfc = getAirtableTextField(fields, 'RFC (PM)', airtableFieldIds.rfcPm, 'RFC (PF)', airtableFieldIds.rfcPf);
      const correo = getAirtableTextField(fields, 'Correo Electrónico', airtableFieldIds.correo);
      const telefono = getAirtableTextField(
        fields,
        'Teléfono',
        airtableFieldIds.telefono,
        'Teléfono (No adjunto Constancia)',
        airtableFieldIds.telefonoValidacion
      );
      const whatsapp = getAirtableTextField(fields, 'WhatsApp', airtableFieldIds.whatsapp, 'WhatsApp Contribuyente');
      const contactarMediante = getAirtableTextField(fields, 'Contactar Mediante');
      const periodo = getAirtableTextField(fields, 'Periodo a Facturar', airtableFieldIds.periodo);
      const numeroTicket = getAirtableTextField(fields, 'Número de Ticket/OC', airtableFieldIds.numeroTicket);
      const concepto = getAirtableTextField(
        fields,
        'Concepto de Venta o Descripción',
        airtableFieldIds.concepto
      );
      const tipoContribuyente = getAirtableTextField(
        fields,
        'Tipo de Contribuyente',
        airtableFieldIds.tipoContribuyente
      );
      const activityDateValue =
        fields['Fecha y hora de subida de factura'] ||
        fields[SOLICITUDES_FIELD_IDS.fechaHoraSubidaFactura] ||
        fields['Fecha de Solicitud'] ||
        record.createdTime;
      const canUploadFiles = canUploadApprovedInvoice({ estado });
      const uploadBlockedReason = getApprovedUploadBlockedReason({ estado });
      const { url: primaryLinkUrl, label: primaryLinkLabel } = resolveInvoicePrimaryLink({
        estado,
        downloadUrl,
        contribuyenteDownloadUrl
      });
      const responsableFilterKey = responsableNormalized || normalizeComparableText(responsableRaw) || 'sin-asignar';
      const responsableFilterLabel =
        responsableDisplay === 'Sin asignar'
          ? 'Sin asignar'
          : responsableRaw && responsableRaw !== responsableDisplay
            ? `${responsableDisplay} · ${responsableRaw}`
            : responsableDisplay;
      const emisor = getAirtableTextField(
        fields,
        'Emisor',
        'Nombre Emisor',
        'Razón social emisor',
        'Razon social emisor'
      );
      const receptor = getAirtableTextField(
        fields,
        'Receptor',
        'Nombre Receptor',
        'Razón social receptor',
        'Razon social receptor'
      );
      return {
        folio: getAirtableTextField(fields, 'Folio', SOLICITUDES_FIELD_IDS.folio) || record.id,
        airtableRecordId: record.id,
        cliente: nombreRazonSocial || empresa || 'Sin nombre',
        nombreContribuyente: nombreContribuyente || '',
        clienteVisible: nombreContribuyente || nombreRazonSocial || empresa || 'Sin nombre',
        nombreRazonSocial,
        idContribuyente,
        empresa,
        clienteAlias,
        estado,
        estadoKey,
        enviadoPorUsuario,
        canToggleUserSent,
        contacto,
        metodoPago,
        metodoPagoCompacto: getCompactPaymentMethodLabel(metodoPago),
        pendientePago,
        pendientePagoKey: normalizeComparableText(pendientePago),
        clienteAsociadoId,
        total: Number.isFinite(totalNumber) ? formatCurrency(totalNumber) : '—',
        emisor: emisor || empresa || 'AC Negocios',
        receptor: receptor || nombreRazonSocial || nombreContribuyente || empresa || 'No disponible',
        responsable: responsableDisplay,
        responsableRaw: responsableRaw || 'Sin asignar',
        responsableFilterKey,
        responsableFilterLabel,
        listaCtesIdCliente,
        rfc,
        correo,
        telefono,
        whatsapp,
        contactarMediante,
        periodo,
        numeroTicket,
        concepto,
        tipoContribuyente,
        activityDateValue,
        actualizado: formatDate(
          fields['Fecha y hora de subida de factura'] ||
          fields[SOLICITUDES_FIELD_IDS.fechaHoraSubidaFactura] ||
          record.createdTime
        ),
        nombreDocumento,
        downloadUrl,
        contribuyenteDownloadUrl,
        primaryLinkUrl,
        primaryLinkLabel,
        canUploadFiles,
        uploadBlockedReason,
        searchIndex: [
          getAirtableTextField(fields, 'Folio', SOLICITUDES_FIELD_IDS.folio) || record.id,
          nombreContribuyente,
          nombreRazonSocial,
          empresa,
          clienteAlias,
          idContribuyente,
          listaCtesIdCliente,
          estado,
          contacto,
          metodoPago,
          pendientePago,
          responsableDisplay,
          responsableRaw,
          rfc,
          correo,
          telefono,
          whatsapp,
          contactarMediante,
          periodo,
          numeroTicket,
          concepto,
          tipoContribuyente,
          nombreDocumento,
          emisor,
          receptor
        ]
          .map(normalizeComparableText)
          .filter(Boolean)
          .join(' ')
      };
    });
    return sortInvoiceRowsByActivity(rows);
  }, [airtableRecords, clientDirectory, userInitialsByEmail]);

  const liveRequestsByRange = useMemo(() => {
    const rangeStart = getRangeStartDate(range);
    if (!rangeStart) return normalizedAirtableRows;
    return normalizedAirtableRows.filter(item => {
      const eventDate = parseDateValue(item.activityDateValue);
      if (!eventDate) return true;
      return eventDate >= rangeStart;
    });
  }, [normalizedAirtableRows, range]);

  const usingLiveData = Boolean(session && hasAirtableConfig);
  const requestsFromRange = usingLiveData ? liveRequestsByRange : mockRequests;
  const scopedFacturacionRequests = useMemo(
    () => filterInvoicesByScope(requestsFromRange, PANEL_SCOPE_CONFIG, summaryFilter),
    [requestsFromRange, summaryFilter]
  );
  const scopedApprovedRequests = useMemo(
    () => filterInvoicesByScope(requestsFromRange, APPROVED_SCOPE_CONFIG, approvedFilter),
    [requestsFromRange, approvedFilter]
  );
  const liveFilterOptions = useMemo(() => {
    return {
      estados: buildDynamicFilterOptions(
        normalizedAirtableRows.map(item => item.estado),
        estadoFilterOptions,
        'Todos los estados',
        STATUS_FILTER_ORDER
      ),
      metodoPago: buildDynamicFilterOptions(
        normalizedAirtableRows.map(item => item.metodoPago),
        metodoPagoFilterOptions,
        'Todos los métodos de pago'
      ),
      contacto: buildDynamicFilterOptions(
        normalizedAirtableRows.map(item => item.contacto),
        contactoFilterOptions,
        'Todos los métodos de contacto',
        CONTACT_FILTER_ORDER
      ),
      pendientePago: buildDynamicFilterOptions(
        normalizedAirtableRows.map(item => item.pendientePago),
        pendientePagoOptions,
        'Pagado y pendiente',
        PAYMENT_STATUS_FILTER_ORDER
      ),
      responsables: buildResponsibleFilterOptions(normalizedAirtableRows)
    };
  }, [normalizedAirtableRows]);
  const resolvedEstadoFilterOptions = usingLiveData ? liveFilterOptions.estados : estadoFilterOptions;
  const resolvedMetodoPagoFilterOptions = usingLiveData ? liveFilterOptions.metodoPago : metodoPagoFilterOptions;
  const resolvedContactoFilterOptions = usingLiveData ? liveFilterOptions.contacto : contactoFilterOptions;
  const resolvedPendientePagoOptions = usingLiveData ? liveFilterOptions.pendientePago : pendientePagoOptions;
  const resolvedResponsableFilterOptions = usingLiveData
    ? liveFilterOptions.responsables
    : buildResponsibleFilterOptions(requestsFromRange);

  const requestsToDisplay = useMemo(() => {
    let list = scopedFacturacionRequests;

    if (tableFilters.estado !== 'all') {
      list = list.filter(item => item.estado === tableFilters.estado);
    }
    if (tableFilters.metodoPago !== 'all') {
      list = list.filter(item => item.metodoPago === tableFilters.metodoPago);
    }
    if (tableFilters.contacto !== 'all') {
      list = list.filter(item => item.contacto === tableFilters.contacto);
    }
    if (tableFilters.pendientePago !== 'all') {
      list = list.filter(item => item.pendientePago === tableFilters.pendientePago);
    }
    if (tableFilters.responsable !== 'all') {
      list = list.filter(item => {
        const responsibleKey =
          item.responsableFilterKey || normalizeComparableText(item.responsableFilterLabel || item.responsable);
        return responsibleKey === tableFilters.responsable;
      });
    }

    if (searchTerm.trim()) {
      list = list.filter(item => matchesInvoiceSearch(item, searchTerm));
    }

    return sortInvoiceRowsByActivity(list);
  }, [scopedFacturacionRequests, tableFilters, searchTerm]);

  const isUsingLiveData = usingLiveData;
  const isUsingLiveAttendanceData = Boolean(session && hasAirtableConfig && canAccessReportes && !attendanceError);
  const attendanceUsersToDisplay = isUsingLiveAttendanceData ? attendanceUsers : attendanceUsersMock;
  const attendanceRecordsToDisplay = isUsingLiveAttendanceData ? attendanceRecords : attendanceRecordsMock;
  const approvedRequestsBase = scopedApprovedRequests;

  const approvedRequests = useMemo(() => {
    let list = approvedRequestsBase;

    if (approvedTableFilters.estado !== 'all') {
      list = list.filter(item => item.estado === approvedTableFilters.estado);
    }
    if (approvedTableFilters.metodoPago !== 'all') {
      list = list.filter(item => item.metodoPago === approvedTableFilters.metodoPago);
    }
    if (approvedTableFilters.contacto !== 'all') {
      list = list.filter(item => item.contacto === approvedTableFilters.contacto);
    }
    if (approvedTableFilters.pendientePago !== 'all') {
      list = list.filter(item => item.pendientePago === approvedTableFilters.pendientePago);
    }
    if (approvedTableFilters.responsable !== 'all') {
      list = list.filter(item => {
        const responsibleKey =
          item.responsableFilterKey || normalizeComparableText(item.responsableFilterLabel || item.responsable);
        return responsibleKey === approvedTableFilters.responsable;
      });
    }

    if (approvedSearchTerm.trim()) {
      list = list.filter(item => matchesInvoiceSearch(item, approvedSearchTerm));
    }

    return sortInvoiceRowsByActivity(list);
  }, [approvedRequestsBase, approvedFilter, approvedTableFilters, approvedSearchTerm]);

  const facturacionScopeOptions = useMemo(() => {
    return PANEL_SCOPE_CONFIG.map(scope => ({
      ...scope,
      count: filterInvoicesByScope(requestsFromRange, PANEL_SCOPE_CONFIG, scope.key).length
    }));
  }, [requestsFromRange]);

  const approvedScopeOptions = useMemo(() => {
    return APPROVED_SCOPE_CONFIG.map(scope => ({
      ...scope,
      count: filterInvoicesByScope(requestsFromRange, APPROVED_SCOPE_CONFIG, scope.key).length
    }));
  }, [requestsFromRange]);

  const summaryCardData = useMemo(() => {
    return PANEL_SCOPE_CONFIG.map(scope => ({
      key: scope.key,
      label: scope.label,
      value: `${filterInvoicesByScope(requestsFromRange, PANEL_SCOPE_CONFIG, scope.key).length}`,
      trend: scope.countLabel
    }));
  }, [requestsFromRange]);

  const approvedSummaryCardData = useMemo(() => {
    return APPROVED_SCOPE_CONFIG.map(scope => ({
      key: scope.key,
      label: scope.label,
      value: `${filterInvoicesByScope(requestsFromRange, APPROVED_SCOPE_CONFIG, scope.key).length}`,
      trend: scope.countLabel
    }));
  }, [requestsFromRange]);

  const hasActiveTableFilters = useMemo(() => {
    return (
      summaryFilter !== 'operativas' ||
      searchTerm.trim().length > 0 ||
      Object.values(tableFilters).some(value => value !== 'all')
    );
  }, [summaryFilter, searchTerm, tableFilters]);

  const hasActiveApprovedTableFilters = useMemo(() => {
    return (
      approvedFilter !== 'all-post-approval' ||
      approvedSearchTerm.trim().length > 0 ||
      Object.values(approvedTableFilters).some(value => value !== 'all')
    );
  }, [approvedFilter, approvedSearchTerm, approvedTableFilters]);

  const facturacionScopeLabel =
    facturacionScopeOptions.find(option => option.key === summaryFilter)?.label || 'En proceso';
  const approvedScopeLabel =
    approvedScopeOptions.find(option => option.key === approvedFilter)?.label || 'Todas aprobadas y en proceso';
  const facturacionResultSummary = `Mostrando ${requestsToDisplay.length} de ${scopedFacturacionRequests.length} solicitudes en "${facturacionScopeLabel}". Busca por folio, RFC, ticket/OC, documento, periodo, correo, WhatsApp o responsable.`;
  const approvedResultSummary = `Mostrando ${approvedRequests.length} de ${approvedRequestsBase.length} facturas en "${approvedScopeLabel}". Busca por folio, RFC, ticket/OC, documento, periodo, correo, WhatsApp o responsable.`;

  const derivedTaxSchedule = useMemo(() => {
    const schedule = {};
    Object.entries(activitySummaryData || {}).forEach(([period, rows]) => {
      schedule[period] = (rows || []).flatMap(row => {
        const clientInfo = row.clientInfo || {};
        const listaCtesIdCliente = row.listaCtesIdCliente || clientInfo.idCliente || '';
        const empresaBase =
          row.listaCtesEmpresa ||
          clientInfo.empresa ||
          row.shortName ||
          row.client ||
          row.clientId ||
          '';
        const folderLabel =
          listaCtesIdCliente && empresaBase
            ? `${empresaBase} - ${listaCtesIdCliente}`
            : empresaBase || row.client;
        const hasClientInfo = clientInfo && Object.keys(clientInfo).length > 0;
          return Object.entries(row.obligations || {}).map(([serviceKey, cell]) => ({
            id: `${row.clientId || row.client}-${serviceKey}-${period}`,
            period,
            activityPeriod: period,
          activity: cell.activity || serviceKey,
          activityKey: serviceKey,
          clientId: row.clientId || '',
          client: row.shortName || row.client || row.clientId || 'Cliente',
          rfc: row.clientId || '',
          empresa: empresaBase || row.client || row.clientId || 'Cliente',
          listaCtesIdCliente,
          listaCtesEmpresa: empresaBase || '',
          clienteAsociadoInfo: hasClientInfo ? clientInfo : null,
          obligation: serviceKey,
          service: serviceKey,
          assignedTo: (cell.responsables || row.responsables || []).join(' / ') || 'Sin asignar',
          responsables: cell.responsables || row.responsables || [],
          responsablesRaw: cell.responsablesRaw || row.responsablesRaw || [],
          responsableEmails: cell.responsableEmails || row.responsableEmails || [],
          requirements: [],
          folder: folderLabel || '',
          dueDate: cell.dueText || cell.dia || '',
          dueLabel: cell.dueText || cell.dia || '',
          dueText: cell.dueText || cell.dia || '',
          dueStatus: cell.status || 'pending',
            status: cell.status || 'Pendiente',
            statusKey: cell.status || 'pending',
            notes: cell.notes || '',
            recordId: cell.recordId || '',
            requiresUpload: Boolean(cell.requiresUpload),
            driveUrl: cell.driveUrl || ''
          }));
        });
    });
    return schedule;
  }, [activitySummaryData]);

  const derivedTaxSummary = useMemo(() => {
    const periodKey = `${todayYear}-${todayMonthKey}`;
    const tasks = derivedTaxSchedule[periodKey] || [];
    const totals = {
      total: tasks.length,
      dueSoon: tasks.filter(t => t.dueStatus === 'dueSoon').length,
      late: tasks.filter(t => t.dueStatus === 'late').length,
      ontrack: tasks.filter(t => t.dueStatus === 'ontrack').length
    };
    if (!tasks.length) return taxUploadSummary;
    return [
      {
        key: 'total',
        label: 'Expedientes recibidos',
        value: `${totals.total}`,
        detail: 'Registrados en Calendario',
        tone: 'success'
      },
      {
        key: 'pending',
        label: 'Pendientes de declarar',
        value: `${totals.dueSoon + totals.late}`,
        detail: 'Prioriza próximos o atrasados',
        tone: 'warning'
      },
      {
        key: 'late',
        label: 'Atrasados',
        value: `${totals.late}`,
        detail: 'Requieren acción inmediata',
        tone: 'danger'
      }
    ];
  }, [derivedTaxSchedule, todayMonthKey, todayYear]);

  const currentTaxPeriodTasks = useMemo(() => {
    const periodKey = `${todayYear}-${todayMonthKey}`;
    return derivedTaxSchedule[periodKey] || [];
  }, [derivedTaxSchedule, todayMonthKey, todayYear]);

  const overviewData = useMemo(() => {
    const requestsTotal = requestsFromRange.length;
    const readyToInvoice = requestsFromRange.filter(item => hasInvoiceStatus(item, ['Aprobada'])).length;
    const sentOrGenerated = requestsFromRange.filter(item => hasInvoiceStatus(item, ['Enviada', 'Factura generada'])).length;
    const inProgressRequests = requestsFromRange.filter(item =>
      hasInvoiceStatus(item, ['Pendiente', 'Aprobada', 'Cliente Notificado'])
    ).length;
    const pendingPayment = requestsFromRange.filter(item =>
      normalizeComparableText(item.pendientePago).includes('pendiente')
    ).length;

    const logsTotal = logs.length;
    const logsOk = logs.filter(item => item.estadoClass === 'ok').length;
    const logsWarning = logs.filter(item => item.estadoClass === 'advertencia').length;
    const logsError = logs.filter(item => item.estadoClass === 'error').length;

    const attendanceTotal = attendanceRecordsToDisplay.length;
    const attendanceOpen = attendanceRecordsToDisplay.filter(record => {
      const status = normalizeComparableText(record.estado);
      return status.includes('abierta') || status.includes('incompleta');
    }).length;
    const attendanceIncidents = attendanceRecordsToDisplay.filter(record => {
      const validation = normalizeComparableText(record.validacion);
      return Boolean(validation) && !validation.includes('valida');
    }).length;

    const taxTotal = currentTaxPeriodTasks.length;
    const taxLate = currentTaxPeriodTasks.filter(task => task.dueStatus === 'late').length;
    const taxDueSoon = currentTaxPeriodTasks.filter(task => task.dueStatus === 'dueSoon').length;
    const taxOnTrack = Math.max(taxTotal - taxLate - taxDueSoon, 0);

    return {
      requestsTotal,
      readyToInvoice,
      sentOrGenerated,
      inProgressRequests,
      pendingPayment,
      logsTotal,
      logsOk,
      logsWarning,
      logsError,
      attendanceTotal,
      attendanceOpen,
      attendanceIncidents,
      taxTotal,
      taxLate,
      taxDueSoon,
      taxOnTrack
    };
  }, [requestsFromRange, logs, attendanceRecordsToDisplay, currentTaxPeriodTasks]);

  const overviewMetrics = useMemo(() => {
    const metrics = [];
    if (canAccessFacturas) {
      const completionPercent = toPercent(
        overviewData.sentOrGenerated + overviewData.readyToInvoice,
        overviewData.requestsTotal,
        100
      );
      const automationPercent = toPercent(overviewData.logsOk, overviewData.logsTotal, 100);
      metrics.push({
        key: 'facturas-activo',
        label: 'Solicitudes activas',
        value: `${overviewData.requestsTotal}`,
        detail: `${overviewData.inProgressRequests} en proceso`,
        delta: `${overviewData.pendingPayment} con pago pendiente`,
        tone: overviewData.inProgressRequests > 0 ? 'warning' : 'success'
      });
      metrics.push({
        key: 'facturas-automatizacion',
        label: 'Automatizacion estable',
        value: `${automationPercent}%`,
        detail: `${overviewData.logsOk}/${overviewData.logsTotal || 0} ejecuciones exitosas`,
        delta: `${overviewData.logsError} errores · ${overviewData.logsWarning} alertas`,
        tone: overviewData.logsError > 0 ? 'danger' : overviewData.logsWarning > 0 ? 'warning' : 'success'
      });
      metrics.push({
        key: 'facturas-avance',
        label: 'Cierre operativo',
        value: `${completionPercent}%`,
        detail: `${overviewData.sentOrGenerated} solicitudes cerradas`,
        delta: `${overviewData.readyToInvoice} listas para facturar`,
        tone: completionPercent >= 75 ? 'success' : completionPercent >= 50 ? 'warning' : 'danger'
      });
    }
    if (canAccessReportes) {
      metrics.push({
        key: 'reportes-incidencias',
        label: 'Incidencias de asistencia',
        value: `${overviewData.attendanceOpen + overviewData.attendanceIncidents}`,
        detail: `${overviewData.attendanceTotal} registros en el rango`,
        delta: `${overviewData.attendanceOpen} abiertos · ${overviewData.attendanceIncidents} atipicos`,
        tone: overviewData.attendanceOpen + overviewData.attendanceIncidents > 0 ? 'warning' : 'success'
      });
    }
    if (canAccessTax) {
      metrics.push({
        key: 'tax-urgencias',
        label: 'Obligaciones urgentes',
        value: `${overviewData.taxLate + overviewData.taxDueSoon}`,
        detail: `${overviewData.taxTotal} tareas del periodo`,
        delta: `${overviewData.taxLate} atrasadas`,
        tone: overviewData.taxLate > 0 ? 'danger' : overviewData.taxDueSoon > 0 ? 'warning' : 'success'
      });
    }
    return metrics;
  }, [canAccessFacturas, canAccessReportes, canAccessTax, overviewData]);

  const overviewOperations = useMemo(() => {
    const rows = [];
    if (canAccessFacturas) {
      const completionPercent = toPercent(
        overviewData.sentOrGenerated + overviewData.readyToInvoice,
        overviewData.requestsTotal,
        100
      );
      rows.push({
        key: 'facturacion',
        module: 'Facturacion',
        focus: 'Solicitudes, aprobaciones y n8n',
        pending: `${overviewData.inProgressRequests} en flujo`,
        performance: `${completionPercent}% de avance`,
        nextAction: overviewData.inProgressRequests
          ? `Priorizar ${overviewData.inProgressRequests} solicitudes pendientes.`
          : 'Flujo controlado; mantener seguimiento de nuevas entradas.',
        tone:
          overviewData.logsError > 0 || overviewData.inProgressRequests > 8
            ? 'danger'
            : overviewData.inProgressRequests > 0 || overviewData.logsWarning > 0
              ? 'warning'
              : 'success',
        boardKey: 'panel',
        actionLabel: 'Abrir'
      });
    }
    if (canAccessReportes) {
      const resolvedAttendance = Math.max(overviewData.attendanceTotal - overviewData.attendanceOpen, 0);
      const attendancePercent = toPercent(resolvedAttendance, overviewData.attendanceTotal, 100);
      rows.push({
        key: 'reportes',
        module: 'Reportes',
        focus: 'Asistencias y validaciones por usuario',
        pending: `${overviewData.attendanceOpen} abiertos`,
        performance: `${attendancePercent}% registros completos`,
        nextAction:
          overviewData.attendanceOpen + overviewData.attendanceIncidents > 0
            ? `Revisar ${overviewData.attendanceIncidents} validaciones atipicas.`
            : 'Sin incidencias detectadas en asistencias.',
        tone:
          overviewData.attendanceOpen > 2
            ? 'danger'
            : overviewData.attendanceOpen + overviewData.attendanceIncidents > 0
              ? 'warning'
              : 'success',
        boardKey: 'reportes',
        actionLabel: 'Abrir'
      });
    }
    if (canAccessTax) {
      const taxPercent = toPercent(overviewData.taxOnTrack, overviewData.taxTotal, 100);
      rows.push({
        key: 'tax',
        module: 'Impuestos',
        focus: 'Calendario fiscal y evidencias XML/PDF',
        pending: `${overviewData.taxLate + overviewData.taxDueSoon} urgentes`,
        performance: `${taxPercent}% en tiempo`,
        nextAction:
          overviewData.taxLate > 0
            ? `Atender ${overviewData.taxLate} obligaciones atrasadas.`
            : overviewData.taxDueSoon > 0
              ? `Programar ${overviewData.taxDueSoon} vencimientos proximos.`
              : 'Periodo fiscal bajo control.',
        tone: overviewData.taxLate > 0 ? 'danger' : overviewData.taxDueSoon > 0 ? 'warning' : 'success',
        boardKey: 'tax',
        actionLabel: 'Abrir'
      });
    }
    return rows;
  }, [canAccessFacturas, canAccessReportes, canAccessTax, overviewData]);

  const overviewPerformance = useMemo(() => {
    const rows = [];
    if (canAccessFacturas) {
      const percent = toPercent(
        overviewData.sentOrGenerated + overviewData.readyToInvoice,
        overviewData.requestsTotal,
        100
      );
      rows.push({
        key: 'performance-facturacion',
        label: 'Facturacion',
        percent,
        detail: `${overviewData.sentOrGenerated} cerradas · ${overviewData.pendingPayment} pendientes de pago`,
        tone: percent >= 75 ? 'success' : percent >= 50 ? 'warning' : 'danger'
      });
    }
    if (canAccessReportes) {
      const resolvedAttendance = Math.max(overviewData.attendanceTotal - overviewData.attendanceOpen, 0);
      const percent = toPercent(resolvedAttendance, overviewData.attendanceTotal, 100);
      rows.push({
        key: 'performance-reportes',
        label: 'Reportes',
        percent,
        detail: `${overviewData.attendanceIncidents} validaciones fuera de norma`,
        tone: overviewData.attendanceOpen + overviewData.attendanceIncidents > 0 ? 'warning' : 'success'
      });
    }
    if (canAccessTax) {
      const percent = toPercent(overviewData.taxOnTrack, overviewData.taxTotal, 100);
      rows.push({
        key: 'performance-tax',
        label: 'Impuestos',
        percent,
        detail: `${overviewData.taxLate} atrasadas · ${overviewData.taxDueSoon} por vencer`,
        tone: overviewData.taxLate > 0 ? 'danger' : overviewData.taxDueSoon > 0 ? 'warning' : 'success'
      });
    }
    return rows;
  }, [canAccessFacturas, canAccessReportes, canAccessTax, overviewData]);

  const overviewBoards = useMemo(() => {
    return controlBoardsForCurrentUser.map(board => {
      if (board.key === 'panel') {
        return {
          ...board,
          highlights: [
            { label: 'Solicitudes activas', value: `${overviewData.requestsTotal}` },
            { label: 'Alertas n8n', value: `${overviewData.logsWarning + overviewData.logsError}` }
          ]
        };
      }
      if (board.key === 'reportes') {
        return {
          ...board,
          highlights: [
            { label: 'Registros capturados', value: `${overviewData.attendanceTotal}` },
            { label: 'Incidencias', value: `${overviewData.attendanceOpen + overviewData.attendanceIncidents}` }
          ]
        };
      }
      if (board.key === 'tax') {
        return {
          ...board,
          highlights: [
            { label: 'Obligaciones periodo', value: `${overviewData.taxTotal}` },
            { label: 'Urgentes', value: `${overviewData.taxLate + overviewData.taxDueSoon}` }
          ]
        };
      }
      return board;
    });
  }, [controlBoardsForCurrentUser, overviewData]);

  const filteredClientOptions = useMemo(() => {
    if (!clientSearch) return clientOptions;
    const query = clientSearch.trim().toLowerCase();
    return clientOptions.filter(option => {
      const haystack = [
        option.alias,
        option.empresa,
        option.nombreComercial,
        option.idCliente,
        getClientLabel(option)
      ].map(value => (value || '').toLowerCase());
      return haystack.some(value => value.includes(query));
    });
  }, [clientOptions, clientSearch]);

  const isPersonaMoral = createForm.tipoContribuyente === 'Persona Moral (Empresa)';
  const isPersonaFisica = createForm.tipoContribuyente === 'Persona Física';
  const isExtranjero = createForm.tipoContribuyente === 'Extranjero';
  const requiresFormaPago = createForm.metodoPago === 'Pago en una sola exhibición (PUE)';
  const constanciaAdjunta = (createForm.constanciaFiles || []).length > 0;
  const whatsappRequired = ['WhatsApp', 'Ambos'].includes(createForm.contact);
  const createFeedbackTone = createFeedback && /correctamente/i.test(createFeedback) ? 'success' : 'error';
  const showSummarySection = ['panel', 'approved'].includes(activeView);
  const isSentView = approvedFilter === 'enviadas';
  const isGeneratedView = approvedFilter === 'factura-generada';
  const isWhatsappView = isSentView || isGeneratedView;
  const uploadAutomationFeedbackTone = uploadAutomationFeedback && /(correctamente|exito|éxito)/i.test(uploadAutomationFeedback) ? 'success' : 'error';
  const showClientSearchWarning = Boolean(
    clientDropdownOpen &&
    !loadingClients &&
    clientOptions.length > 0 &&
    clientSearch.trim() &&
    !filteredClientOptions.length
  );


  useEffect(() => {
    if (!createForm.clienteAsociado) {
      setClientSearch('');
      return;
    }
    const selected = clientOptions.find(option => option.id === createForm.clienteAsociado);
    if (selected) {
      const label = getClientLabel(selected);
      if (clientSearch !== label) {
        setClientSearch(label);
      }
    }
  }, [createForm.clienteAsociado, clientOptions]);

  useEffect(() => {
    if (!session) return;
    if (!hasAirtableConfig) {
      console.log('[Dashboard] Sin credenciales Airtable, usando datos de ejemplo');
      return;
    }

    console.log('[Dashboard] Registros obtenidos de Airtable:', airtableRecords);
    if (!airtableRecords.length) {
      console.log('[Dashboard] Sin datos en vivo, mostrando mocks para rango', range);
    }
  }, [airtableRecords, range, session]);

  const validateSolicitudStep = () => {
    if (!createForm.clientName.trim()) {
      return 'Ingresa el nombre o razón social del contribuyente.';
    }
    if (isPersonaMoral && createForm.rfcPm.trim().length !== 12) {
      return 'El RFC para persona moral debe contener 12 caracteres.';
    }
    if (isPersonaFisica && createForm.rfcPf.trim().length !== 13) {
      return 'El RFC para persona física debe contener 13 caracteres.';
    }
    const postalCode = String(createForm.codigoPostal || '').trim();
    if (!/^\d{5}$/.test(postalCode)) {
      return 'Captura un código postal válido de 5 dígitos.';
    }
    if (regimenOptions.length && !createForm.regimenFiscal) {
      return 'Selecciona el Régimen Fiscal.';
    }
    if (usoCfdiOptions.length && !createForm.usoCfdi) {
      return 'Selecciona el uso del CFDI.';
    }
    if (!createForm.numeroTicket.trim()) {
      return 'Indica el número de TICKET/OC.';
    }
    if (!createForm.metodoPago) {
      return 'Selecciona el método de pago.';
    }
    if (requiresFormaPago && !createForm.formaPago) {
      return 'Selecciona la forma de pago.';
    }
    const totalValue = Number(createForm.total);
    if (Number.isNaN(totalValue) || totalValue <= 0) {
      return 'Captura el total estimado en MXN.';
    }
    if (!createForm.concepto.trim()) {
      return 'Describe el concepto de venta o servicios.';
    }
    if (!constanciaAdjunta && !createForm.telefonoValidacion.trim()) {
      return 'Proporciona un teléfono si no adjuntas constancia fiscal.';
    }
    return '';
  };

  const validateContactoStep = () => {
    if (!createForm.email.trim()) {
      return 'Ingresa un correo electrónico válido.';
    }
    const normalizedWhatsapp = formatWhatsappValue(createForm.whatsapp);
    if (whatsappRequired && !WHATSAPP_PATTERN.test(normalizedWhatsapp)) {
      return 'Ingresa un número de WhatsApp con el formato +52XXXXXXXXXX.';
    }
    if (!whatsappRequired && createForm.whatsapp.trim() && !WHATSAPP_PATTERN.test(normalizedWhatsapp)) {
      return 'El número de WhatsApp debe tener 10 dígitos después de +52.';
    }
    return '';
  };

  const handleNextStep = () => {
    const error = validateSolicitudStep();
    if (error) {
      setCreateFeedback(error);
      return;
    }
    setCreateFeedback('');
    setCreateStep(2);
  };

  const handlePrevStep = () => {
    setCreateStep(1);
    setCreateFeedback('');
  };

  const handleAuthenticate = async (email, password) => {
    if (!firebaseReady) {
      throw new Error('Configura Firebase antes de iniciar sesión');
    }
    const activeSession = await signIn(email, password);
    setSession(activeSession);
  };

  const handleLogout = async () => {
    setIsSessionMenuOpen(false);
    if (!firebaseReady) return;
    await signOut();
    setSession(null);
    setShowCreateForm(false);
    setAirtableRecords([]);
    setCreateFeedback('');
  };

  const handleToggleSessionMenu = () => {
    setIsSessionMenuOpen(prev => !prev);
  };

  const handleCloseSessionMenu = () => {
    setIsSessionMenuOpen(false);
  };

  const handleOpenCreate = () => {
    if (!canAccessFacturas) return;
    setIsSessionMenuOpen(false);
    setShowCreateForm(true);
    setCreateStep(1);
    setCreateFeedback('');
  };

  const handleCloseCreate = () => {
    if (creatingRecord) return;
    setShowCreateForm(false);
    setCreateForm(getCreateFormDefaults());
    setCreateStep(1);
    setCreateFeedback('');
    setFileInputsKey(prev => prev + 1);
  };

  const handleSummarySelect = key => {
    if (!key) return;
    setSummaryFilter(key);
  };

  const handleApprovedSummarySelect = key => {
    if (!key) return;
    setApprovedFilter(key);
  };

  const handleViewChange = key => {
    if (!key || !availableViews.includes(key)) return;
    setIsSessionMenuOpen(false);
    setActiveView(key);
  };

  const handleBoardNavigation = key => {
    if (!key) return;
    handleViewChange(key);
  };

  const updateTableFilter = (key, value) => {
    setTableFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearTableFilters = () => {
    setSummaryFilter('operativas');
    setTableFilters({
      estado: 'all',
      metodoPago: 'all',
      contacto: 'all',
      pendientePago: 'all',
      responsable: 'all'
    });
    setSearchTerm('');
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  const handleRequestStatusChange = async (recordId, nextStatus) => {
    if (!recordId || !nextStatus || !isCurrentUserAdmin || !isUsingLiveData) return;

    const currentRecord = airtableRecords.find(record => record.id === recordId);
    const currentStatus = getAirtableTextField(currentRecord?.fields, 'Estado de Solicitud', airtableFieldIds.estado);
    if (currentStatus === nextStatus) {
      setRequestStatusFeedbackById(prev => ({
        ...prev,
        [recordId]: null
      }));
      return;
    }

    setUpdatingRequestStatusById(prev => ({ ...prev, [recordId]: true }));
    setRequestStatusFeedbackById(prev => ({ ...prev, [recordId]: null }));

    try {
      const updatedRecord = await updateAirtableRecord({
        tableId: AIRTABLE_TABLES.solicitudes,
        recordId,
        fields: {
          [airtableFieldIds.estado]: nextStatus
        }
      });

      setAirtableRecords(prev =>
        prev.map(record => {
          if (record.id !== recordId) return record;
          return {
            ...record,
            ...updatedRecord,
            fields: {
              ...(record.fields || {}),
              ...(updatedRecord?.fields || {}),
              'Estado de Solicitud': nextStatus,
              [airtableFieldIds.estado]: nextStatus
            }
          };
        })
      );
      setRequestStatusFeedbackById(prev => ({
        ...prev,
        [recordId]: {
          type: 'success',
          message: 'Estado actualizado'
        }
      }));
    } catch (error) {
      setRequestStatusFeedbackById(prev => ({
        ...prev,
        [recordId]: {
          type: 'error',
          message: error?.message || 'No fue posible actualizar el estado.'
        }
      }));
    } finally {
      setUpdatingRequestStatusById(prev => ({
        ...prev,
        [recordId]: false
      }));
    }
  };

  const handleApprovedUserSentChange = async (recordId, nextValue) => {
    if (!recordId || !isUsingLiveData || !canAccessFacturas) return;

    const currentRecord = airtableRecords.find(record => record.id === recordId);
    const currentStatus = getAirtableTextField(currentRecord?.fields, 'Estado de Solicitud', airtableFieldIds.estado);
    const currentStatusKey = normalizeComparableText(currentStatus);
    if (!['factura generada', 'enviada'].includes(currentStatusKey)) return;

    const nextChecked = Boolean(nextValue);
    const currentChecked = getAirtableCheckboxField(
      currentRecord?.fields || {},
      'Enviado por Usuario',
      airtableFieldIds.enviadoPorUsuario
    );
    if (currentChecked === nextChecked) {
      setApprovedUserSentFeedbackById(prev => ({
        ...prev,
        [recordId]: null
      }));
      return;
    }

    setUpdatingApprovedUserSentById(prev => ({ ...prev, [recordId]: true }));
    setApprovedUserSentFeedbackById(prev => ({
      ...prev,
      [recordId]: null
    }));

    try {
      const updatedRecord = await updateAirtableRecord({
        tableId: AIRTABLE_TABLES.solicitudes,
        recordId,
        fields: {
          [airtableFieldIds.enviadoPorUsuario]: nextChecked
        }
      });

      setAirtableRecords(prev =>
        prev.map(record => {
          if (record.id !== recordId) return record;
          return {
            ...record,
            ...updatedRecord,
            fields: {
              ...(record.fields || {}),
              ...(updatedRecord?.fields || {}),
              'Enviado por Usuario': nextChecked,
              [airtableFieldIds.enviadoPorUsuario]: nextChecked
            }
          };
        })
      );
      setApprovedUserSentFeedbackById(prev => ({
        ...prev,
        [recordId]: {
          type: 'success',
          message: nextChecked ? 'Marcada como enviada por usuario' : 'Marca eliminada'
        }
      }));
    } catch (error) {
      setApprovedUserSentFeedbackById(prev => ({
        ...prev,
        [recordId]: {
          type: 'error',
          message: error?.message || 'No fue posible actualizar la confirmacion.'
        }
      }));
    } finally {
      setUpdatingApprovedUserSentById(prev => ({
        ...prev,
        [recordId]: false
      }));
    }
  };

  const handleClientSearchChange = event => {
    const value = event.target.value;
    setClientSearch(value);
    setClientDropdownOpen(true);
    if (!value.trim()) {
      setCreateForm(prev => ({ ...prev, clienteAsociado: '' }));
    }
  };

  const handleClientSelect = option => {
    if (!option) return;
    setCreateForm(prev => ({ ...prev, clienteAsociado: option.id }));
    setClientSearch(getClientLabel(option));
    setClientDropdownOpen(false);
  };

  const handleFormChange = event => {
    const { name, value } = event.target;
    setCreateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleWhatsappBlur = () => {
    setCreateForm(prev => {
      const formatted = formatWhatsappValue(prev.whatsapp);
      if (formatted === prev.whatsapp) return prev;
      return { ...prev, whatsapp: formatted };
    });
  };

  const handleFileChange = event => {
    const { name, files } = event.target;
    if (!name) return;
    const normalizedFiles = Array.from(files || []).slice(0, 5);
    setCreateForm(prev => ({ ...prev, [name]: normalizedFiles }));
  };

  const handleClearFiles = fieldName => {
    setCreateForm(prev => ({ ...prev, [fieldName]: [] }));
    setFileInputsKey(prev => prev + 1);
  };

  const [showWhatsappPreview, setShowWhatsappPreview] = useState(false);
  const [previewRequest, setPreviewRequest] = useState(null);
  const handleOpenAirtableEmbed = () => {
    if (!canOpenAirtable) return;
    setShowAirtableEmbed(true);
  };
  const handleCloseAirtableEmbed = () => setShowAirtableEmbed(false);
  const handleRefreshAirtableRecords = () => {
    setIsSessionMenuOpen(false);
    setReloadIndex(prev => prev + 1);
  };

  const resetAutomationUploadState = () => {
    setXmlAutomationFile(null);
    setPdfAutomationFile(null);
    setUploadAutomationFeedback('');
    setUploadingAutomation(false);
  };

  const handleOpenAutomationUpload = request => {
    if (!request) return;
    if (!request.canUploadFiles) return;
    setSelectedApprovedRequest(request);
    resetAutomationUploadState();
    setShowAutomationUploadModal(true);
  };

  const handleCloseAutomationUpload = () => {
    resetAutomationUploadState();
    setSelectedApprovedRequest(null);
    setShowAutomationUploadModal(false);
  };

  const handleAutomationFileInputChange = event => {
    const { name, files } = event.target;
    const file = files && files[0] ? files[0] : null;
    if (name === 'xmlFile') {
      setXmlAutomationFile(file);
    }
    if (name === 'pdfFile') {
      setPdfAutomationFile(file);
    }
  };

  const normalizeClientValue = value => {
    if (value === undefined || value === null) return '';
    return String(value).trim().toLowerCase();
  };

  const findClientInfoInDirectory = request => {
    if (!request) return null;
    const linkedClientId = request.clienteAsociadoId;
    if (linkedClientId && clientDirectory[linkedClientId]) {
      return clientDirectory[linkedClientId];
    }
    const candidates = [
      request.clienteAlias,
      request.empresa,
      request.cliente
    ]
      .map(normalizeClientValue)
      .filter(Boolean);
    if (!candidates.length) return null;
    return (
      Object.values(clientDirectory).find(info => {
        const haystack = [
          info.alias,
          info.empresa,
          info.nombreComercial,
          info.idCliente,
          info.name
        ]
          .map(normalizeClientValue)
          .filter(Boolean);
        return haystack.some(value => candidates.includes(value));
      }) || null
    );
  };

  const resolveApprovedRequestEmpresa = request => {
    if (!request) return '';
    const info = request.clienteAsociadoInfo || findClientInfoInDirectory(request);
    if (info) {
      return info.empresa || info.name || request.cliente || request.empresa || '';
    }
    if (request.empresa) return request.empresa;
    return request.cliente || '';
  };

  const handleUploadApprovedFiles = async event => {
    event?.preventDefault?.();
    if (!selectedApprovedRequest) return;
    if (uploadingAutomation) return;
    if (!selectedApprovedRequest.canUploadFiles) {
      setUploadAutomationFeedback(
        selectedApprovedRequest.uploadBlockedReason ||
          'La solicitud ya no permite una nueva carga de archivos.'
      );
      return;
    }
    if (!xmlAutomationFile && !pdfAutomationFile) {
      setUploadAutomationFeedback('Selecciona al menos un archivo XML o PDF.');
      return;
    }
    if (!N8N_WEBHOOK_URL) {
      setUploadAutomationFeedback('Configura REACT_APP_N8N_WEBHOOK_URL para enviar archivos al flujo de n8n.');
      return;
    }
    try {
      setUploadingAutomation(true);
      setUploadAutomationFeedback('');
      const folio = selectedApprovedRequest.folio || 'SIN-FOLIO';
      const clienteAsociadoInfo = findClientInfoInDirectory(selectedApprovedRequest);
      const empresa = resolveApprovedRequestEmpresa({
        ...selectedApprovedRequest,
        clienteAsociadoInfo: clienteAsociadoInfo || undefined
      });
      const requestPayload = {
        ...selectedApprovedRequest,
        folio,
        empresa,
        clienteAsociadoInfo
      };
      console.log('[n8n] Preparando carga de archivos aprobados:', {
        folio,
        empresa,
        tieneXml: Boolean(xmlAutomationFile),
        tienePdf: Boolean(pdfAutomationFile)
      });
      const response = await sendApprovedRequestFilesToN8n({
        xmlFile: xmlAutomationFile,
        pdfFile: pdfAutomationFile,
        request: requestPayload,
        webhookUrl: N8N_WEBHOOK_URL,
        triggeredBy: session?.email || session?.displayName || 'dashboard',
        executedAt: new Date().toISOString()
      });
      const normalizedResponseStatus = normalizeComparableText(response?.status || 'ok');
      const isSuccess = Boolean(response?.ok) && normalizedResponseStatus === 'ok';
      if (isSuccess) {
        console.log('[n8n] Webhook respondió OK:', response);
        setUploadAutomationFeedback('Archivos enviados con éxito a n8n.');
        setReloadIndex(prev => prev + 1);
        if (reloadTimerRef.current) {
          clearTimeout(reloadTimerRef.current);
        }
        reloadTimerRef.current = setTimeout(() => {
          setReloadIndex(prev => prev + 1);
        }, 15000);
        setTimeout(() => {
          handleCloseAutomationUpload();
        }, 1200);
      } else {
        const fallbackMessage = typeof response === 'string'
          ? response
          : 'n8n respondió sin estado OK, revisa la automatización.';
        console.warn('[n8n] Webhook respondió sin estado OK:', response);
        setUploadAutomationFeedback(fallbackMessage);
      }
    } catch (error) {
      console.error('[n8n] Error al enviar archivos:', error);
      setUploadAutomationFeedback(error.message || 'No fue posible enviar los archivos a n8n');
    } finally {
      setUploadingAutomation(false);
    }
  };

  const handleOpenWhatsappPreview = request => {
    if (!request) return;
    setPreviewRequest(request);
    setShowWhatsappPreview(true);
  };

  const handleCloseWhatsappPreview = () => {
    setPreviewRequest(null);
    setShowWhatsappPreview(false);
  };

  const updateApprovedTableFilter = (field, value) => {
    setApprovedTableFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearApprovedFilters = () => {
    setApprovedFilter('all-post-approval');
    setApprovedTableFilters({
      estado: 'all',
      metodoPago: 'all',
      contacto: 'all',
      pendientePago: 'all',
      responsable: 'all'
    });
    setApprovedSearchTerm('');
  };

  const buildWhatsappTemplate = request => {
    const clientName = request?.nombreContribuyente || request?.clienteVisible || request?.cliente || 'cliente';
    const folio = request?.folio || 'tu factura';
    const emisor = request?.emisor || request?.empresa || 'AC Negocios';
    const receptor = request?.receptor || request?.clienteVisible || request?.cliente || 'No disponible';
    const total = request?.total || 'No disponible';
    const documentName = request?.nombreDocumento || '';
    const link = request?.downloadUrl || '';
    const lines = [
      `Hola ${clientName},`,
      '',
      'Le confirmamos que su factura ya está disponible.',
      '',
      'Detalle:',
      `- Folio: ${folio}`,
      `- Emisor: ${emisor}`,
      `- Receptor: ${receptor}`,
      `- Total facturado: ${total}`,
      `- Documento: ${documentName || 'No especificado'}`,
      '',
      link ? `Descarga de factura: ${link}` : 'Agrega el enlace de descarga para completar el mensaje.',
      '',
      'Si requiere algún ajuste o tiene dudas, puede responder a este mensaje o escribir al 6145333267.',
      '',
      'Gracias por confiar en AC Negocios.',
      'Atentamente,',
      'Equipo de AC Negocios'
    ];
    return lines.join('\n');
  };

  const handleSendWhatsapp = request => {
    if (!request?.downloadUrl) {
      return;
    }
    const template = buildWhatsappTemplate(request);
    const url = `https://wa.me/?text=${encodeURIComponent(template)}`;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    handleCloseWhatsappPreview();
  };

  const handleCreateSubmit = async event => {
    event.preventDefault();
    if (!session) {
      setCreateFeedback('Inicia sesión nuevamente para registrar solicitudes');
      return;
    }
    if (!hasAirtableConfig) {
      setCreateFeedback('Configura las credenciales de Airtable para guardar registros');
      return;
    }

    const solicitudError = validateSolicitudStep();
    if (solicitudError) {
      setCreateFeedback(solicitudError);
      setCreateStep(1);
      return;
    }
    const contactoError = validateContactoStep();
    if (contactoError) {
      setCreateFeedback(contactoError);
      setCreateStep(2);
      return;
    }

    try {
      setCreatingRecord(true);
      setCreateFeedback('');

      const payload = {};
      const assignField = (fieldId, value) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'string' && value.trim() === '') return;
        payload[fieldId] = value;
      };
      const assignNumber = (fieldId, value) => {
        if (value === undefined || value === null || value === '') return;
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return;
        payload[fieldId] = numeric;
      };

      assignField(airtableFieldIds.nombreRazon, createForm.clientName.trim());
      assignField(airtableFieldIds.clienteAsociado, createForm.clienteAsociado ? [createForm.clienteAsociado] : undefined);
      assignField(airtableFieldIds.tipoContribuyente, createForm.tipoContribuyente);
      if (isPersonaMoral) {
        assignField(airtableFieldIds.rfcPm, createForm.rfcPm.trim());
      }
      if (isPersonaFisica) {
        assignField(airtableFieldIds.rfcPf, createForm.rfcPf.trim());
      }
      if (isExtranjero) {
        assignField(airtableFieldIds.idFiscal, createForm.idFiscal.trim());
      }
      assignField(airtableFieldIds.codigoPostal, String(createForm.codigoPostal || '').trim());
      assignField(airtableFieldIds.numeroTicket, createForm.numeroTicket.trim());
      assignField(airtableFieldIds.periodo, createForm.periodo.trim());
      assignField(airtableFieldIds.correo, createForm.email.trim());
      assignField(airtableFieldIds.telefono, createForm.phone.trim());
      assignField(airtableFieldIds.telefonoValidacion, createForm.telefonoValidacion.trim());
      if (createForm.regimenFiscal) {
        payload[airtableFieldIds.regimenFiscal] = [createForm.regimenFiscal];
      }
      if (createForm.usoCfdi) {
        payload[airtableFieldIds.usoCfdi] = [createForm.usoCfdi];
      }
      const normalizedWhatsapp = formatWhatsappValue(createForm.whatsapp);
      assignField(airtableFieldIds.whatsapp, normalizedWhatsapp);
      assignField(airtableFieldIds.contacto, createForm.contact);
      assignField(airtableFieldIds.metodoPago, createForm.metodoPago);
      assignField(airtableFieldIds.formaPago, createForm.formaPago);
      assignNumber(airtableFieldIds.total, createForm.total);
      assignField(airtableFieldIds.concepto, createForm.concepto.trim());
      assignField(airtableFieldIds.estado, createForm.estado);
      assignField(airtableFieldIds.comentarios, createForm.comentarios.trim());
      assignField(airtableFieldIds.notasInternas, createForm.notasInternas.trim());

      const record = await createInvoiceRequest(payload);
      const recordId = record?.id;
      if (!recordId) {
        throw new Error('Airtable no devolvió un ID de registro al crear la solicitud');
      }

      const uploadAllAttachments = async () => {
        const constanciaFiles = createForm.constanciaFiles || [];
        const ticketFiles = createForm.ticketFiles || [];

        for (const file of constanciaFiles) {
          await uploadRecordAttachment({
            recordId,
            fieldId: airtableFieldIds.constanciaAdjunta,
            file
          });
        }

        for (const file of ticketFiles) {
          await uploadRecordAttachment({
            recordId,
            fieldId: airtableFieldIds.ticketAdjunto,
            file
          });
        }
      };

      await uploadAllAttachments();
      setCreateFeedback('Solicitud registrada correctamente');
      setReloadIndex(prev => prev + 1);
      setCreateForm(getCreateFormDefaults());
      setCreateStep(1);
      setFileInputsKey(prev => prev + 1);
      setTimeout(() => {
        handleCloseCreate();
      }, 900);
    } catch (error) {
      setCreateFeedback(error.message);
    } finally {
      setCreatingRecord(false);
    }
  };

  if (!firebaseReady) {
    return (
      <>
        {themeToggleButton}
        <div className="auth-shell">
          <div className="login-card">
            <p className="eyebrow">Configuración requerida</p>
            <h1>Agrega tus credenciales de Firebase</h1>
            <p className="subtitle">
              Define las variables REACT_APP_FIREBASE_* en tu archivo .env para habilitar el inicio de sesión.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!authReady) {
    return (
      <>
        {themeToggleButton}
        <div className="auth-shell">
          <div className="login-card">
            <p className="eyebrow">Control de acceso</p>
            <h1>Conectando con Firebase Auth...</h1>
            <p className="subtitle">Estamos validando si tu sesión sigue activa. Esto tomará unos segundos.</p>
          </div>
        </div>
      </>
    );
  }

  if (!session) {
    return (
      <>
        {themeToggleButton}
        <LoginPanel
          onAuthenticate={handleAuthenticate}
          externalError={authError}
        />
      </>
    );
  }

  return (
    <div className="app-shell" data-ui-view={activeView} data-ui-section="app-shell">
      {themeToggleButton}
      <header className="app-header" data-ui-section="app-header">
        <div>
          <p className="eyebrow">Equipo AC Negocios</p>
          <h1>Control Interno</h1>
        </div>
        <div className="header-actions" data-ui-section="header-actions">
          <div className="session-dropdown" ref={sessionMenuRef}>
            <button
              type="button"
              className={`session-trigger ${isSessionMenuOpen ? 'open' : ''}`}
              onClick={handleToggleSessionMenu}
              aria-haspopup="menu"
              aria-expanded={isSessionMenuOpen}
              aria-label="Opciones de sesión"
              title="Opciones de sesión"
              data-ui-action="toggle-session-menu"
            >
              <PersonIcon className="header-icon" />
              <ChevronDownIcon className="session-chevron" />
            </button>
            {isSessionMenuOpen && (
              <div className="session-menu" role="menu" aria-label="Opciones de sesión">
                <p className="session-menu-email">{sessionUserEmail}</p>
                <div className="session-menu-links">
                  {userMenuScreens.map(screen => (
                    <button
                      key={screen.id}
                      type="button"
                      className="session-menu-link"
                      onClick={() => {
                        if (screen.viewKey) {
                          handleViewChange(screen.viewKey);
                        }
                        handleCloseSessionMenu();
                      }}
                    >
                      {screen.label}
                    </button>
                  ))}
                </div>
                <button type="button" className="session-menu-action" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
          <button
            className={`btn ghost header-icon-btn ${loadingRecords ? 'is-loading' : ''}`}
            onClick={handleRefreshAirtableRecords}
            disabled={loadingRecords}
            aria-label={loadingRecords ? 'Actualizando datos' : 'Refrescar'}
            title={loadingRecords ? 'Actualizando datos' : 'Refrescar'}
            data-ui-action="refresh-records"
          >
            <RefreshIcon className="header-icon" />
          </button>
          {canAccessFacturas && (
            <button
              className="btn primary header-icon-btn"
              onClick={handleOpenCreate}
              aria-label="Nueva factura"
              title="Nueva factura"
              data-ui-action="open-create-modal"
            >
              <FilePlusIcon className="header-icon" />
            </button>
          )}
        </div>
      </header>

      <section data-ui-section="navigation-shell">
        <div
          className="view-tabs-shell"
          data-ui-tabs="primary"
          data-overflow-left={viewTabsOverflow.left ? 'true' : 'false'}
          data-overflow-right={viewTabsOverflow.right ? 'true' : 'false'}
        >
          <div className="view-tabs" ref={viewTabsRef} role="tablist" aria-label="Navegación principal">
            <button
              type="button"
              className={`pill ${activeView === 'overview' ? 'active' : ''}`}
              onClick={() => handleViewChange('overview')}
              data-ui-action="view-overview"
            >
              Inicio
            </button>
            {CONCILIATION_SECTION_ENABLED && (
              <button
                type="button"
                className={`pill ${activeView === 'conciliation' ? 'active' : ''}`}
                onClick={() => handleViewChange('conciliation')}
                data-ui-action="view-conciliation"
              >
                Conciliacion
              </button>
            )}
            {FINANCIAL_STATEMENTS_SECTION_ENABLED && (
              <button
                type="button"
                className={`pill ${activeView === 'financial-statements' ? 'active' : ''}`}
                onClick={() => handleViewChange('financial-statements')}
                data-ui-action="view-estados-financieros"
              >
                Estados financieros
              </button>
            )}
            {canAccessLinks && (
              <button
                type="button"
                className={`pill ${activeView === 'databases' ? 'active' : ''}`}
                onClick={() => handleViewChange('databases')}
                data-ui-action="view-databases"
              >
                Enlaces
              </button>
            )}
            {canAccessFacturas && (
              <button
                type="button"
                className={`pill ${activeView === 'panel' ? 'active' : ''}`}
                onClick={() => handleViewChange('panel')}
                data-ui-action="view-facturacion"
              >
                Facturacion
              </button>
            )}
            {canAccessFacturas && (
              <button
                type="button"
                className={`pill ${activeView === 'approved' ? 'active' : ''}`}
                onClick={() => handleViewChange('approved')}
                data-ui-action="view-aprobadas"
              >
                Aprobadas
              </button>
            )}
            {canAccessReportes && (
              <button
                type="button"
                className={`pill ${activeView === 'reportes' ? 'active' : ''}`}
                onClick={() => handleViewChange('reportes')}
                data-ui-action="view-reportes"
              >
                Reportes
              </button>
            )}
            {canAccessTax && (
              <button
                type="button"
                className={`pill ${activeView === 'tax' ? 'active' : ''}`}
                onClick={() => handleViewChange('tax')}
                data-ui-action="view-impuestos"
              >
                Impuestos
              </button>
            )}
            {canAccessFiniquitos && (
              <button
                type="button"
                className={`pill ${activeView === 'finiquitos' ? 'active' : ''}`}
                onClick={() => handleViewChange('finiquitos')}
                data-ui-action="view-finiquitos"
              >
                Finiquitos
              </button>
            )}
            {CATALOGO_SAT_SECTION_ENABLED && (
              <button
                type="button"
                className={`pill ${activeView === 'catalogo-sat' ? 'active' : ''}`}
                onClick={() => handleViewChange('catalogo-sat')}
                data-ui-action="view-catalogo-sat"
              >
                Catálogo SAT
              </button>
            )}
          </div>
        </div>
        {showSummarySection && (
          <>
            <div className="filters" data-ui-section="range-filter">
              <div>
                <p className="label">Rango de análisis</p>
                <strong>{activeLabel}</strong>
              </div>
              <div className="pill-group">
                {timeRanges.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    className={`pill ${option.key === range ? 'active' : ''}`}
                    onClick={() => setRange(option.key)}
                    data-ui-action={`range-${option.key}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <SummaryCards
              cards={activeView === 'panel' ? summaryCardData : approvedSummaryCardData}
              activeKey={activeView === 'panel' ? summaryFilter : approvedFilter}
              onSelect={activeView === 'panel' ? handleSummarySelect : handleApprovedSummarySelect}
            />
          </>
        )}
      </section>

      <CreateInvoiceModal
        open={showCreateForm}
        onClose={handleCloseCreate}
        onSubmit={handleCreateSubmit}
        createStep={createStep}
        onNextStep={handleNextStep}
        onPrevStep={handlePrevStep}
        createForm={createForm}
        onFormChange={handleFormChange}
        onWhatsappBlur={handleWhatsappBlur}
        contactOptions={contactoFormOptions}
        metodoPagoOptions={metodoPagoOptions}
        estadoOptions={estadoSolicitudOptions}
        formaPagoOptions={formaPagoOptions}
        clientSearch={clientSearch}
        onClientSearchChange={handleClientSearchChange}
        clientDropdownOpen={clientDropdownOpen}
        onClientDropdownOpen={() => setClientDropdownOpen(true)}
        onClientDropdownClose={() => setClientDropdownOpen(false)}
        filteredClientOptions={filteredClientOptions}
        onClientSelect={handleClientSelect}
        loadingClients={loadingClients}
        clientsError={clientsError}
        showClientSearchWarning={showClientSearchWarning}
        isPersonaMoral={isPersonaMoral}
        isPersonaFisica={isPersonaFisica}
        isExtranjero={isExtranjero}
        regimenOptions={regimenOptions}
        usoCfdiOptions={usoCfdiOptions}
        loadingCatalogs={loadingCatalogs}
        catalogsError={catalogsError}
        requiresFormaPago={requiresFormaPago}
        constanciaAdjunta={constanciaAdjunta}
        fileInputsKey={fileInputsKey}
        onFileChange={handleFileChange}
        onClearFiles={handleClearFiles}
        whatsappRequired={whatsappRequired}
        createFeedback={createFeedback}
        createFeedbackTone={createFeedbackTone}
        creatingRecord={creatingRecord}
      />

      {activeView === 'overview' && (
        <OverviewView
          boards={overviewBoards}
          metrics={overviewMetrics}
          operations={overviewOperations}
          performance={overviewPerformance}
          onSelectBoard={handleBoardNavigation}
          title={overviewTitle}
          description={overviewDescription}
        />
      )}

      {CONCILIATION_SECTION_ENABLED && activeView === 'conciliation' && (
        <ConciliacionBancariaView />
      )}

      {FINANCIAL_STATEMENTS_SECTION_ENABLED && activeView === 'financial-statements' && (
        <EstadosFinancierosView />
      )}

      {activeView === 'finiquitos' && canAccessFiniquitos && <FiniquitosView session={session} />}

      {CATALOGO_SAT_SECTION_ENABLED && activeView === 'catalogo-sat' && <CatalogoSatView />}

      {activeView === 'databases' && canAccessLinks && (
        <DatabasesView
          NOCODB_URL={NOCODB_URL}
          AIRTABLE_BASE_URL={AIRTABLE_BASE_URL}
          VAULTWARDEN_URL={VAULTWARDEN_URL}
          canOpenAirtable={canOpenAirtable}
        />
      )}

      {activeView === 'panel' && canAccessFacturas && (
        <FacturacionView
          showAutomationPulse={showAutomationPulse}
          setShowAutomationPulse={setShowAutomationPulse}
          activeLabel={activeLabel}
          isUsingLiveData={isUsingLiveData}
          canOpenAirtable={canOpenAirtable}
          handleOpenAirtableEmbed={handleOpenAirtableEmbed}
          tableFilters={tableFilters}
          updateTableFilter={updateTableFilter}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          estadoFilterOptions={resolvedEstadoFilterOptions}
          metodoPagoFilterOptions={resolvedMetodoPagoFilterOptions}
          contactoFilterOptions={resolvedContactoFilterOptions}
          pendientePagoOptions={resolvedPendientePagoOptions}
          responsableFilterOptions={resolvedResponsableFilterOptions}
          scopeOptions={facturacionScopeOptions}
          activeScope={summaryFilter}
          handleScopeChange={handleSummarySelect}
          filterSummaryText={facturacionResultSummary}
          hasActiveFilters={hasActiveTableFilters}
          handleClearFilters={handleClearTableFilters}
          loadingRecords={loadingRecords}
          errorRecords={errorRecords}
          requestsToDisplay={requestsToDisplay}
          canManageRequestStatuses={Boolean(isCurrentUserAdmin && isUsingLiveData)}
          requestStatusOptions={estadoSolicitudOptions}
          updatingRequestStatusById={updatingRequestStatusById}
          requestStatusFeedbackById={requestStatusFeedbackById}
          handleRequestStatusChange={handleRequestStatusChange}
          N8N_APP_URL={N8N_APP_URL}
          handleRefreshLogs={handleRefreshLogs}
          loadingLogs={loadingLogs}
          logsError={logsError}
          logs={logs}
        />
      )}

      {canAccessTax && activeView === 'tax' && (
        <ImpuestosView
          derivedTaxSummary={derivedTaxSummary}
          taxUploadMonths={taxUploadMonths}
          taxUploadYears={taxUploadYears}
          derivedTaxSchedule={derivedTaxSchedule}
          taxDueLegend={taxDueLegend}
          taxUploadChecklist={taxUploadChecklist}
          activitySummaryData={activitySummaryData}
          activityStatusLegend={activityStatusLegend}
          session={session}
          activityResponsablesDirectory={activityResponsablesDirectory}
          userDirectory={userDirectory}
          clientDirectory={clientDirectory}
          CALENDARIO_TABLE_ID={CALENDARIO_TABLE_ID}
        />
      )}

      {activeView === 'approved' && canAccessFacturas && (
        <AprobadasView
          approvedTableFilters={approvedTableFilters}
          updateApprovedTableFilter={updateApprovedTableFilter}
          approvedSearchTerm={approvedSearchTerm}
          setApprovedSearchTerm={setApprovedSearchTerm}
          estadoFilterOptions={resolvedEstadoFilterOptions}
          metodoPagoFilterOptions={resolvedMetodoPagoFilterOptions}
          contactoFilterOptions={resolvedContactoFilterOptions}
          pendientePagoOptions={resolvedPendientePagoOptions}
          responsableFilterOptions={resolvedResponsableFilterOptions}
          scopeOptions={approvedScopeOptions}
          activeScope={approvedFilter}
          handleScopeChange={handleApprovedSummarySelect}
          filterSummaryText={approvedResultSummary}
          hasActiveFilters={hasActiveApprovedTableFilters}
          handleClearFilters={handleClearApprovedFilters}
          approvedRequests={approvedRequests}
          canManageUserSent={Boolean(isUsingLiveData && canAccessFacturas)}
          updatingUserSentById={updatingApprovedUserSentById}
          userSentFeedbackById={approvedUserSentFeedbackById}
          handleUserSentChange={handleApprovedUserSentChange}
          isWhatsappView={isWhatsappView}
          handleOpenWhatsappPreview={handleOpenWhatsappPreview}
          handleOpenAutomationUpload={handleOpenAutomationUpload}
        />
      )}

      {activeView === 'reportes' && canAccessReportes && (
        <ReportesView
          enabled={Boolean(session && hasAirtableConfig)}
          isAdmin={isCurrentUserAdmin}
          locationsTableId={ATTENDANCE_LOCATIONS_TABLE_ID}
          fieldConfig={ATTENDANCE_LOCATION_FIELD_CONFIG}
          attendanceUsersToDisplay={attendanceUsersToDisplay}
          attendanceRecordsToDisplay={attendanceRecordsToDisplay}
          attendanceTemplateOptions={attendanceTemplateOptions}
          attendanceUserTemplates={attendanceUserTemplates}
          defaultTemplateFile={DEFAULT_ATTENDANCE_TEMPLATE_FILE}
          asistenciasTableId={ASISTENCIAS_TABLE_ID}
          onRefresh={() => setReloadIndex(prev => prev + 1)}
          loadingAttendance={loadingAttendance}
          attendanceError={attendanceError}
          isUsingLiveAttendanceData={isUsingLiveAttendanceData}
        />
      )}

      {showAirtableEmbed && canOpenAirtable && (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Tablero de Airtable"
          onClick={handleCloseAirtableEmbed}
        >
          <div className="airtable-modal" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Solicitudes en Airtable</h3>
                <p>Consulta o edita la vista original sin salir del dashboard.</p>
              </div>
              <button type="button" className="link-btn" onClick={handleCloseAirtableEmbed}>
                Cerrar
              </button>
            </div>
            <div className="airtable-frame">
              <iframe
                title="Embedded Airtable view"
                className="airtable-embed"
                src={AIRTABLE_EMBED_URL}
                frameBorder="0"
                width="100%"
                height="533"
              />
            </div>
          </div>
        </div>
      )}

      {showAutomationUploadModal && selectedApprovedRequest && (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Enviar archivos para automatización"
          onClick={handleCloseAutomationUpload}
        >
          <form className="automation-upload-card" onClick={event => event.stopPropagation()} onSubmit={handleUploadApprovedFiles}>
            <div className="modal-header">
              <div>
                <h3>Adjuntar archivos</h3>
                <p>Sube el XML y/o PDF para {selectedApprovedRequest.folio}</p>
              </div>
              <button type="button" className="link-btn" onClick={handleCloseAutomationUpload}>
                Cerrar
              </button>
            </div>

            <p className="helper-text">
              {selectedApprovedRequest.canUploadFiles
                ? 'Los archivos se enviarán a n8n para su carga en Drive y actualización de Airtable.'
                : selectedApprovedRequest.uploadBlockedReason}
            </p>

            {selectedApprovedRequest.canUploadFiles && (
              <div className="automation-upload-grid">
                <label>
                  XML timbrado
                  <input
                    type="file"
                    name="xmlFile"
                    accept=".xml,application/xml,text/xml"
                    onChange={handleAutomationFileInputChange}
                    disabled={uploadingAutomation}
                  />
                  {xmlAutomationFile && <span className="file-chip">{xmlAutomationFile.name}</span>}
                </label>
                <label>
                  PDF de la factura
                  <input
                    type="file"
                    name="pdfFile"
                    accept=".pdf,application/pdf"
                    onChange={handleAutomationFileInputChange}
                    disabled={uploadingAutomation}
                  />
                  {pdfAutomationFile && <span className="file-chip">{pdfAutomationFile.name}</span>}
                </label>
              </div>
            )}

            {uploadAutomationFeedback && (
              <p className={`helper-text ${uploadAutomationFeedbackTone}`}>{uploadAutomationFeedback}</p>
            )}

            <div className="modal-actions">
              <button type="button" className="btn ghost" onClick={handleCloseAutomationUpload} disabled={uploadingAutomation}>
                Cancelar
              </button>
              {selectedApprovedRequest.canUploadFiles && (
                <button type="submit" className="btn primary" disabled={uploadingAutomation}>
                  {uploadingAutomation ? 'Enviando...' : 'Enviar a n8n'}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {showWhatsappPreview && previewRequest && (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Previsualización WhatsApp"
          onClick={handleCloseWhatsappPreview}
        >
          <div className="whatsapp-modal" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Enviar por WhatsApp</h3>
                <p>Confirma el mensaje y el enlace antes de enviarlo.</p>
              </div>
              <button type="button" className="link-btn" onClick={handleCloseWhatsappPreview}>
                Cerrar
              </button>
            </div>
            <div className="whatsapp-preview">
              <p className="label">Así verá el cliente:</p>
              <div className="whatsapp-bubble">
                {(() => {
                  const lines = buildWhatsappTemplate(previewRequest).split('\n');
                  const downloadLineIndex = lines.findIndex(line => line.startsWith('Descarga de factura:'));
                  const bodyLines =
                    downloadLineIndex >= 0 ? lines.filter((_, index) => index !== downloadLineIndex) : lines;
                  return bodyLines.map((line, index) => (
                    <span key={`preview-line-${index}`}>{line}</span>
                  ));
                })()}
                {previewRequest.downloadUrl ? (
                  <a href={previewRequest.downloadUrl} target="_blank" rel="noopener noreferrer">
                    Descarga de factura: {previewRequest.downloadUrl}
                  </a>
                ) : (
                  <em>Agrega el “Enlace descarga factura” en Airtable.</em>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn ghost" onClick={handleCloseWhatsappPreview}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => handleSendWhatsapp(previewRequest)}
                disabled={!previewRequest.downloadUrl}
              >
                Enviar ahora
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
